import { categoryLabel, PRODUCT_CATEGORIES, migrateCategorySelection } from "../config/productCategories";
import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useTransition,
  memo,
  type TouchEvent,
  type CSSProperties,
  type UIEvent,
} from "react";
import { createPortal } from "react-dom";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { Settings, RotateCcw, CreditCard, ExternalLink, X, TrendingUp, ChevronLeft, ChevronRight, SlidersHorizontal, Sparkles, Flame, EyeOff, Plus } from "lucide-react";
import ScanningEmptyState from "../components/ScanningEmptyState";
import EmailOptInCard from "../components/EmailOptInCard";
import DealCardGuide from "../components/DealCardGuide";
import TopProductsView from "../components/TopProductsView";
import { useLayoutMode } from "../utils/layoutModePreference";
import { CheckStockContext } from "../context/CheckStockContext";
import { ProductOfferChips, ProductStatusBadge, getProductStatus } from "../components/ProductDealBadges";
import { buildTopProductsViewModel } from "../utils/topProducts";

import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual";

/** Stable `style` ref for `ProductItem` in the list so `memo` can bail on parent re-renders. */
const PRODUCT_ITEM_LIST_STYLE: CSSProperties = {};
import {
  getProductsV2,
  getProductsV2QueryKey,
  browseProducts,
  getOnlineProducts,
  getOnlineProductsQueryKey,
  getPublicAffiliateLink,
  type ProductBrowseResponse,
} from "../services/ProductsService";
import { useFlatFavoritesWithRetailer, useRemoveFavoriteWithRetailer } from "../hooks/useFavorites";
import type { ProductListItem } from "../types/Product";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCommunitySuccesses,
  getCommunitySuccessesQueryKey,
} from "../services/CommunitySuccessService";
import { toast } from "react-toastify";
import {
  searchProduct,
  searchHomeDepotProduct,
  searchLowesNearby,
  type StoreProductResult,
  type HomeDepotStoreProductResult,
} from "../services/ProductSearchService";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { useRetailerConfig } from "../hooks/useRetailerConfig";
import { useRetailer } from "../hooks/useRetailer";
import type { CategoryV2 } from "../services/CategoriesService";
import { useHomeDepotCategories } from "../hooks/useBotFilters";
import { hasPennyBetaAccess, isFreeTier, isUserSetUp } from "../utils/userUtils";
import {
  getUserStoresForRetailer,
  isExtraOnlineRetailer,
  supportsCrazyDeals,
  supportsOnlineAndNearbyModes,
  supportsPennyItems,
  type RetailerType,
} from "../config/retailers";
import ProductItem from "../components/ProductItem";
import { ProductOfferPrice } from "../components/ProductOfferPrice";
import ProductSkeleton from "../components/ProductSkeleton";
import TopProductsSkeleton from "../components/TopProductsSkeleton";
import ProductsHeader from "../components/ProductsHeader";
import { hasActiveFilters } from "../components/ProductsHeader.utils";
import SavedKeywordsButton from "../components/SavedKeywordsButton";
import { getUserRetailers } from "../services/UserService";

import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";
import EmptyState from "../components/EmptyState";
import NearbyResults from "../components/NearbyResults";
import ZohoWidget from "../components/ZohoWidget";
import ProductDetailModal from "../components/ProductDetailModal";
import HiddenProductsModal from "../components/HiddenProductsModal";
import HiddenProductToast from "../components/HiddenProductToast";
import { getProductDetailIdentifier } from "../components/ProductDetailModal.utils";
import { useZohoWidget } from "../hooks/useZohoWidget";
import { useSwipeDetection } from "../hooks/useSwipeDetection";
import { isIosLikeDevice } from "../hooks/useViewportOffsetReset";
import ZipCodeModal from "../components/ZipCodeModal";
import PennyItemsDisclaimerModal from "../components/PennyItemsDisclaimerModal";
import PennyModeChooser from "../components/PennyModeChooser";
import BotFiltersOnboardingModal from "../components/BotFiltersOnboardingModal";
import DealsAccessSwitcher from "../components/DealsAccessSwitcher";
import NearbySavingsHero from "../components/NearbySavingsHero";
import ReferralInviteModal from "../components/ReferralInviteModal";

// LOCAL DEVELOPMENT OVERRIDE. Production commit b8b68829 keeps this false; do not push true.
const MEMBER_REFERRALS_ENABLED = true;
import { useScrollPosition } from "../hooks/useScrollPosition";
import { formatExtraStoreId, getExtraStores } from "../utils/extraStores";
import {
  activeHiddenExtraStoreIds,
  excludeHiddenExtraStoreProducts,
  splitExtraStores,
} from "../utils/hiddenExtraStores";
import { useHiddenExtraStores } from "../hooks/useHiddenExtraStores";
import { useStores, type StoreData } from "../hooks/useStores";
import { getRecentWootProducts } from "../services/WootService";
import {
  applyBaseFilters,
  buildFavoritesAsProducts,
  buildProductsByUpc,
  buildSearchTokens,
  buildStoreFilterOptions,
  appendAdminStoreOption,
  appendExtraStoreOptions,
  buildTargetOnlineProducts,
  buildWalmartOnlineProducts,
  buildWootProducts,
  computeProductStatusCountsFromBaseProducts,
  deriveCategories,
  applyStatusAndStoreFilters,
  filterAndSortProducts,
  getApiStoreId,
  getProductSearchHaystack,
  hasPublicOnlineFeedAccess,
  getRetailerAccess,
  getRetailerLabel,
  isOnlineRetailer as isOnlineRetailerRetailer,
  computeTrendingCategories,
  emptyProductBrowsePage,
  mergeDollarGeneralBrowseResponses,
  parseDollarGeneralCouponCursor,
  shouldLoadDollarGeneralCouponFeed,
  type TrendingCategory,
} from "./ProductsPage.utils";
import type { StatusValue } from "../components/ProductsHeader.utils";
import { getProductImageUrl } from "../utils/imageUtils";
import SaveButton from "../components/buttons/SaveButton";
import {
  formatFirstAppeared,
  formatLastUpdated,
  formatPrice,
  formatStoreId,
  getDiscountPercent,
  getMultiBuyPriceDisplay,
  getLastUpdatedTimestamp,
  getLatestValue,
} from "../components/ProductItem.utils";
import { sanitizeSavedKeywords } from "../utils/savedKeywords";
import { getHiddenProductId, isProductHidden } from "../utils/hiddenProducts";
import type { HiddenProduct } from "../services/HiddenProductsService";
import { useHiddenProducts } from "../hooks/useHiddenProducts";
import {
  getFreeIntentCopy,
  getFreeUserIntent,
  setFreeUserIntent,
  type FreeUserIntent,
} from "../utils/freeExperience";
import {
  getGuestDealResumeIntent,
  getGuestDealUpgradePath,
} from "../utils/authReturn";
import { useHeldCounts } from "../hooks/useHeldCounts";
import { samsClubStockLabel } from "../utils/stockCount";

// Filter localStorage persistence keyed by retailer:store or retailer:online
interface SavedFilters {
  selectedCategories?: string[];
  minStorePrice?: number;
  maxStorePrice?: number;
  minMsrp?: number;
  maxMsrp?: number;
  minDiscount?: number;
  showNewestOnly?: boolean;
  showInStockOnly?: boolean;
  showVariantsOnly?: boolean;
  hideCashback?: boolean;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
}

// Empty array = every category selected. Saved "none" selections reset to all.
const normalizeCategorySelection = (values?: string[]): string[] => {
  const migrated = (migrateCategorySelection(values) ?? []).filter((id) => id !== "__none__");
  return migrated.length === PRODUCT_CATEGORIES.length ? [] : migrated;
};

const getFilterStoreType = (storeId: string) => storeId === "online" ? "online" : "store";
const getFiltersKey = (retailer: string, storeId: string) => `dh_filters:${retailer}:${getFilterStoreType(storeId)}`;
// Online lists default to Best deal (server sortBy "bestDeal": percent off x
// dollars saved with a category mix) so 80%-off apparel cannot own the first
// screens. Woot sorts client-side and keeps its newest-first default.
const getDefaultSortBy = (retailer: string, storeId: string) =>
  (storeId === "online" || isOnlineRetailerRetailer(retailer)) && retailer !== "woot" ? "bestDeal" : "lastUpdated";
const EXPAND_SCROLL_LOCK_MS = 220;
const GLOBAL_SELECTED_SAVED_KEYWORDS_KEY = "dealhawk_selected_saved_keywords";
const PRODUCTS_DEAL_VIEW_MODE_KEY = "dealhawk_products_view_mode";
const DEFAULT_HIDE_CASHBACK = true;
const TOP_PRODUCTS_WINDOW_HOURS = 24;

type DealViewMode = "top" | "all";

const loadDealViewMode = (): DealViewMode => {
  try {
    const raw = localStorage.getItem(PRODUCTS_DEAL_VIEW_MODE_KEY);
    return raw === "all" ? "all" : "top";
  } catch {
    /* localStorage unavailable */
    return "top";
  }
};

const PRODUCTS_PAGE_PERF_LOG_KEY = "dh_debug_products_perf";
const PRODUCTS_PAGE_PERF_THRESHOLD_MS = 8;
const PRODUCTS_PAGE_CACHE_SEPARATOR = "\u001f";

const productsPageCacheIds = new WeakMap<object, number>();
let nextProductsPageCacheId = 1;
const productsPageComputationCache = new WeakMap<object, Map<string, unknown>>();

const getProductsPageCacheId = (value?: object | null) => {
  if (!value) return 0;
  const existing = productsPageCacheIds.get(value);
  if (existing) return existing;
  const next = nextProductsPageCacheId;
  nextProductsPageCacheId += 1;
  productsPageCacheIds.set(value, next);
  return next;
};

const makeProductsPageCacheKey = (
  label: string,
  ...parts: Array<string | number | boolean | null | undefined>
) => [label, ...parts.map((part) => String(part ?? ""))].join(PRODUCTS_PAGE_CACHE_SEPARATOR);

const getCachedProductsPageComputation = <T,>(
  scope: object | undefined | null,
  key: string,
  compute: () => T
): T => {
  if (!scope) return compute();
  let scopedCache = productsPageComputationCache.get(scope);
  if (!scopedCache) {
    scopedCache = new Map<string, unknown>();
    productsPageComputationCache.set(scope, scopedCache);
  }
  if (scopedCache.has(key)) return scopedCache.get(key) as T;
  const value = compute();
  scopedCache.set(key, value);
  return value;
};

const loadSavedFilters = (key: string): SavedFilters => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const saveFiltersToStorage = (key: string, filters: SavedFilters) => {
  try { localStorage.setItem(key, JSON.stringify(filters)); } catch {
    // Storage can be unavailable in private browsing or restricted contexts.
  }
};

const loadSelectedSavedKeywords = (): string[] => {
  try {
    const raw = localStorage.getItem(GLOBAL_SELECTED_SAVED_KEYWORDS_KEY);
    return raw ? sanitizeSavedKeywords(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
};

const saveSelectedSavedKeywords = (keywords: string[]) => {
  try {
    localStorage.setItem(
      GLOBAL_SELECTED_SAVED_KEYWORDS_KEY,
      JSON.stringify(sanitizeSavedKeywords(keywords))
    );
  } catch {
    // Storage can be unavailable in private browsing or restricted contexts.
  }
};

const shouldLogProductsPagePerf = () => {
  if (typeof window === "undefined") return false;
  return import.meta.env.DEV || window.localStorage.getItem(PRODUCTS_PAGE_PERF_LOG_KEY) === "1";
};

const logProductsPagePerf = (
  label: string,
  startedAt: number,
  details: Record<string, unknown>
) => {
  if (!shouldLogProductsPagePerf()) return;
  const durationMs = performance.now() - startedAt;
  if (durationMs < PRODUCTS_PAGE_PERF_THRESHOLD_MS) return;
  console.debug(`[ProductsPage perf] ${label}`, {
    durationMs: Number(durationMs.toFixed(1)),
    ...details,
  });
};

interface ProductsPageProps {
  storeId?: string;
  category?: string;
}

// Store filtering options
interface StoreFilterOption {
  id: string;
  name: string;
  isAll?: boolean;
  isExtra?: boolean;
  distanceMiles?: number;
}

interface GalleryProductCardProps {
  product: ProductListItem;
  retailer: string;
  storeFilterOptions: StoreFilterOption[];
  onHideProduct?: (product: ProductListItem) => void;
}

function FreePreviewWelcomeCard({
  title,
  subtitle,
  onDismiss,
  onUpgrade,
  t,
}: {
  title: string;
  subtitle: string;
  onDismiss: () => void;
  onUpgrade: () => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  return (
    <div className="mx-2 mt-2 rounded-2xl border border-purple-200 bg-gradient-to-br from-white via-purple-50/70 to-indigo-50/80 p-4 shadow-sm dark:border-purple-800/50 dark:from-dark-900 dark:via-purple-950/20 dark:to-dark-900">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 rounded-full bg-purple-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
            <Sparkles className="h-3.5 w-3.5" />
            {t("products.freePreviewBadge")}
          </div>
          <h2 className="mt-3 text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
          <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-dark-300">{subtitle}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-600 shadow-sm dark:bg-dark-800 dark:text-dark-200">
              {t("products.freeSavedDealIncluded")}
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-600 shadow-sm dark:bg-dark-800 dark:text-dark-200">
              {t("products.freeOnlinePreview")}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white hover:text-gray-600 dark:hover:bg-dark-800 dark:hover:text-dark-100"
          aria-label={t("products.freeDismissWelcome")}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onUpgrade}
          className="rounded-xl bg-purple-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-700"
        >
          {t("products.freeUnlockLocalDeals")}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:border-purple-300 hover:text-purple-700 dark:border-zinc-700 dark:bg-dark-800 dark:text-dark-200 dark:hover:border-purple-700 dark:hover:text-purple-300"
        >
          {t("products.freeKeepBrowsing")}
        </button>
      </div>
    </div>
  );
}

function FreeHintRow({
  variant,
  hiddenCount,
  zipCode,
  onUpgrade,
  t,
}: {
  variant: "local" | "more-deals" | "alerts";
  hiddenCount?: number;
  zipCode?: string;
  onUpgrade: () => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  const text =
    variant === "local"
      ? zipCode
        ? t("products.freeHintLocalDealsNearZip", { zip: zipCode })
        : t("products.freeHintLocalDeals")
      : variant === "more-deals"
      ? hiddenCount
        ? t("products.freeHintMoreDealsCount", { count: hiddenCount })
        : t("products.freeHintMoreDeals")
      : t("products.freeHintAlerts");
  return (
    <div className="px-1 pb-1">
      <button
        type="button"
        onClick={onUpgrade}
        className="w-full flex items-center justify-between gap-2 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200/80 dark:border-purple-800/40 px-3 py-2.5 text-left active:scale-[0.98] transition-transform"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-3 h-3 text-purple-400 flex-shrink-0" />
          <span className="text-[13px] font-medium text-gray-700 dark:text-dark-200">{text}</span>
        </div>
        <span className="text-[12px] font-bold text-purple-600 dark:text-purple-400 flex-shrink-0">{t("products.freeHintUnlock")} &rarr;</span>
      </button>
    </div>
  );
}

const GalleryProductCard = memo(({
  product,
  retailer,
  storeFilterOptions,
  onHideProduct,
}: GalleryProductCardProps) => {
  const { t } = useTranslation();
  const priceHistory =
    retailer === "bestbuy" && product.openBox && product.openBoxPrice?.length
      ? product.openBoxPrice
      : product.storePrice;
  const latestPrice = getLatestValue(priceHistory);
  const latestStock = getLatestValue(product.salesFloor);
  const msrp = product.msrp || 0;
  const multiBuy = getMultiBuyPriceDisplay(product, latestPrice, msrp);
  // Dollar General BOGO / mix-and-match: headline on its own line, chips wrap beneath.
  const stackedOffer = Boolean(multiBuy) && retailer === "dollar_general";
  const discountPercent = getDiscountPercent(msrp, latestPrice);
  const isOnlineRetailer =
    retailer === "walmart_online" ||
    retailer === "target_online" ||
    retailer === "amazon" ||
    retailer === "kohls" ||
    retailer === "woot" ||
    isExtraOnlineRetailer(retailer);
  const isWoot = retailer === "woot";
  const isTarget = retailer === "target";
  const isPennyItem = (retailer === "home_depot" || retailer === "dollar_general") && product.storeId?.includes("-PENNY-ITEM");
  const isPotentialPennyItem = retailer === "dollar_general" && isPennyItem;
  const storeId = formatStoreId(product.storeId || "");
  const storeName = storeFilterOptions.find((option) => option.id === storeId)?.name;
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const retailerLabel = retailer === "home_depot"
    ? "HD"
    : isTarget || retailer === "target_online"
    ? "Target"
    : retailer === "dollar_general"
    ? "DG"
    : retailer === "bestbuy"
    ? "BestBuy"
    : retailer === "costco"
    ? "Costco"
    : retailer === "amazon"
    ? "Amazon"
    : retailer === "lowes"
    ? "Lowe's"
    : retailer === "tractor_supply"
    ? "Tractor Supply"
    : retailer === "northern_tool"
    ? "Northern Tool"
    : retailer === "samsclub"
    ? "Sam's Club"
    : retailer === "ace_hardware"
    ? "Ace Hardware"
    : retailer === "kohls"
    ? "Kohl's"
    : retailer === "newegg"
    ? "Newegg"
    : retailer === "gamestop"
    ? "GameStop"
    : retailer === "bjs"
    ? "BJ's Wholesale"
    : retailer === "meijer"
    ? "Meijer"
    : retailer === "menards"
    ? "Menards"
    : retailer === "academy"
    ? "Academy Sports"
    : retailer === "walgreens"
    ? "Walgreens"
    : retailer === "zappos"
    ? "Zappos"
    : retailer === "jcpenney"
    ? "JCPenney"
    : retailer === "belk"
    ? "Belk"
    : retailer === "michaels"
    ? "Michaels"
    : retailer === "nike"
    ? "Nike"
    : retailer === "adidas"
    ? "Adidas"
    : retailer === "reebok"
    ? "Reebok"
    : retailer === "skechers"
    ? "Skechers"
    : retailer === "puma"
    ? "Puma"
    : retailer === "underarmour"
    ? "Under Armour"
    : retailer === "columbia"
    ? "Columbia"
    : retailer === "nordstromrack"
    ? "Nordstrom Rack"
    : retailer === "aldo"
    ? "Aldo"
    : retailer === "vitacost"
    ? "Vitacost"
    : retailer === "journeys"
    ? "Journeys"
    : retailer === "shopdisney"
    ? "shopDisney"
    : retailer === "ninjakitchen"
    ? "Ninja Kitchen"
    : retailer === "hottopic"
    ? "Hot Topic"
    : retailer === "officedepot"
    ? "Office Depot"
    : retailer === "barnesnoble"
    ? "Barnes & Noble"
    : retailer === "razer"
    ? "Razer"
    : retailer === "pacsun"
    ? "PacSun"
    : retailer === "bathbodyworks"
    ? "Bath & Body Works"
    : retailer === "staples"
    ? "Staples"
    : retailer === "petsmart"
    ? "PetSmart"
    : retailer === "ulta"
    ? "Ulta Beauty"
    : retailer === "forever21"
    ? "Forever 21"
    : retailer === "gap"
    ? "Gap"
    : retailer === "petco"
    ? "Petco"
    : retailer === "americaneagle"
    ? "American Eagle"
    : retailer === "aerie"
    ? "Aerie"
    : retailer === "carters"
    ? "Carter's"
    : retailer === "abercrombie"
    ? "Abercrombie & Fitch"
    : retailer === "hollister"
    ? "Hollister"
    : retailer === "oldnavy"
    ? "Old Navy"
    : retailer === "bananarepublic"
    ? "Banana Republic"
    : retailer === "stevemadden"
    ? "Steve Madden"
    : retailer === "athleta"
    ? "Athleta"
    : retailer === "gapfactory"
    ? "Gap Factory"
    : retailer === "bananarepublicfactory"
    ? "Banana Republic Factory"
    : retailer === "boxlunch"
    ? "BoxLunch"
    : retailer === "verabradley"
    ? "Vera Bradley"
    : retailer === "footlocker"
    ? "Foot Locker"
    : retailer === "champs"
    ? "Champs Sports"
    : isWoot
    ? "Woot"
    : "Walmart";
  const lastUpdatedTimestamp = getLastUpdatedTimestamp({
    priceHistory,
    backroomHistory: product.backroom,
    salesFloorHistory: product.salesFloor,
    lastUpdates: product.lastUpdates,
    firstInserted: product.firstInserted,
    lastUpdated: product.lastUpdated,
  });
  const lastUpdatedLabel = formatLastUpdated([lastUpdatedTimestamp], { emptyLabel: t("products.noUpdates"), t });
  const pennyFirstSpottedLabel = formatFirstAppeared(product.firstInserted, { t });
  const [showProductDetailModal, setShowProductDetailModal] = useState(false);
  const [isDetailModalAnimating, setIsDetailModalAnimating] = useState(false);

  const handleAffiliateClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!product.productKey || affiliateLoading) return;
    const tab = window.open("about:blank", "_blank");
    setAffiliateLoading(true);
    try {
      const trackingUrl = await getPublicAffiliateLink(product.productKey);
      if (tab) tab.location.href = trackingUrl;
      else window.location.href = trackingUrl;
    } catch {
      if (tab) tab.close();
    } finally {
      setAffiliateLoading(false);
    }
  };

  const handleOpenProductDetailModal = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setShowProductDetailModal(true);
    setTimeout(() => setIsDetailModalAnimating(true), 10);
  };

  const handleCloseProductDetailModal = () => {
    setIsDetailModalAnimating(false);
    setTimeout(() => setShowProductDetailModal(false), 300);
  };

  // Extract city from store name
  const displayStoreName = (() => {
    if (!storeName) return "";
    const parts = storeName.split(",");
    if (parts.length >= 2) return parts[1].trim();
    const words = storeName.split(" ").filter((w) => !w.startsWith("#") && !w.match(/^Store$/i));
    return words.length > 0 ? words.slice(0, 3).join(" ") : "";
  })();

  return (
    <>
    {/* Soft card: no outline, framed photo, fixed one-line rows, footer pinned to the bottom. */}
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpenProductDetailModal}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") handleOpenProductDetailModal();
      }}
      aria-label={product.name}
      data-deal-guide="card"
      className="group h-full flex flex-col cursor-pointer rounded-[18px] bg-white p-1.5 shadow-[0_4px_10px_rgba(28,21,48,0.08)] transition-shadow hover:shadow-[0_8px_20px_rgba(28,21,48,0.12)] focus-visible:outline-2 focus-visible:outline-purple-500 dark:bg-[#292524] dark:shadow-none dark:ring-1 dark:ring-white/5"
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden rounded-[14px] border border-[#ECEAF2] bg-white dark:border-white/10">
        {product.image ? (
          <img
            src={getProductImageUrl(product.image, "300")}
            alt=""
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-400 dark:text-dark-500">
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
        )}
        {/* Product shots are mostly white; take the glare off in dark mode. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden bg-black/10 dark:block" />
        {isPotentialPennyItem ? (
          <span className="absolute left-2 top-2 rounded-md bg-amber-100 px-2 py-1 text-[10px] font-extrabold tracking-wide text-amber-800">
            {t("products.potentialPennyBadge")}
          </span>
        ) : !isWoot ? (
          <ProductStatusBadge kind={getProductStatus(product, priceHistory)} className="absolute left-2 top-2" />
        ) : null}
        <div className="absolute right-1.5 top-1.5 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {onHideProduct && (
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onHideProduct(product);
              }}
              className="hidden rounded-full bg-white/95 p-2 text-gray-500 shadow-md transition-opacity hover:text-gray-900 group-hover:opacity-100 md:flex md:opacity-0 dark:bg-[#1a1a1a]/90 dark:text-white/80"
              aria-label={t("products.hideProductNamed", { name: product.name })}
              title={t("products.hideProduct")}
            >
              <EyeOff className="h-4 w-4" />
            </button>
          )}
          <SaveButton
            productId={product.upc}
            productName={product.name}
            productImage={product.image}
            productCategory={product.category}
            productStoreId={isOnlineRetailer ? "online" : product.storeId}
            compact={true}
            className="!p-1.5 !bg-white/95 dark:!bg-[#1a1a1a]/90 rounded-full shadow-md hover:!bg-white"
            walmartOnlineData={isOnlineRetailer ? {
              msrp: product.msrp,
              currentPrice: latestPrice || product.msrp,
              currentPrices: priceHistory,
              url: product.url || "",
              sku: product.sku,
              lastUpdated: product.firstInserted,
              lastUpdates: product.lastUpdates,
              discountPercentage: product.discountPercentage,
            } : undefined}
          />
        </div>
        {discountPercent > 0 && !multiBuy && (
          <span data-deal-guide="discount" className="absolute bottom-2 left-2 flex h-[22px] items-center rounded-full bg-red-600 px-2 text-[11px] font-extrabold tabular-nums text-white">
            −{discountPercent}%
          </span>
        )}
      </div>

      {/* Product Info */}
      <div className="flex flex-1 flex-col gap-1 px-1.5 pb-1 pt-2.5">
        <div className={`flex min-w-0 gap-1.5 ${stackedOffer ? "flex-wrap items-center gap-y-1" : "items-baseline"}`}>
          {isPennyItem ? (
            <span className="text-[20px] font-extrabold leading-6 tracking-tight tabular-nums text-red-600 dark:text-red-400">$0.01</span>
          ) : multiBuy ? (
            <ProductOfferPrice display={multiBuy} size={stackedOffer ? "card" : "row"} />
          ) : (
            <span data-deal-guide="price" className="text-[20px] font-extrabold leading-6 tracking-tight tabular-nums text-emerald-700 dark:text-emerald-400">
              {formatPrice(latestPrice)}
            </span>
          )}
          {!multiBuy && msrp > latestPrice && (
            <span className="truncate text-xs tabular-nums text-gray-500 line-through dark:text-dark-400">{formatPrice(msrp)}</span>
          )}
        </div>
        <p className="line-clamp-2 text-[13px] font-medium leading-[18px] text-gray-900 dark:text-dark-50" title={product.name}>
          {product.name}
        </p>
        {isTarget && !product.discountedProduct && (
          <span className="self-start rounded-md bg-purple-50 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
            {t("card.clearanceInStore")}
          </span>
        )}
        <ProductOfferChips product={product} hideOffer={stackedOffer} />
        {isOnlineRetailer || isWoot ? (
          <p className="truncate text-[11px] leading-[15px] text-gray-500 dark:text-dark-400">
            {t("topFeed.online")} · {lastUpdatedLabel}
          </p>
        ) : (
          <>
            <p data-deal-guide="store" className="truncate text-[11px] font-bold leading-[15px] text-purple-700 dark:text-purple-300">
              {storeId ? (displayStoreName ? `${displayStoreName} - #${storeId}` : `#${storeId}`) : "\u00a0"}
            </p>
            <p className="truncate text-[11px] leading-[15px] text-gray-500 dark:text-dark-400">
              {retailer === "walmart" && product.pickupAvailable && (
                <>
                  <span className="font-bold text-sky-700 dark:text-sky-300">{t("products.pickupShort")}</span>
                  {" · "}
                </>
              )}
              {isPennyItem ? (
                <>{t("products.firstSpottedPenny")}: {pennyFirstSpottedLabel}</>
              ) : (
                <>
                  <span className={product.aisle ? "font-semibold text-gray-900 dark:text-dark-50" : ""}>
                    {product.aisle ? t("topFeed.aisle", { aisle: product.aisle }) : t("products.noLocation")}
                  </span>
                  {" · "}
                  {lastUpdatedLabel}
                </>
              )}
            </p>
          </>
        )}

        {/* Footer: stock + the same "+" as the list row (opens details). */}
        <div className="!mt-auto flex items-center gap-1.5 pt-1.5">
          <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs font-bold">
            {isPennyItem ? (
              <span className="truncate text-amber-700 dark:text-amber-300">{t("products.possibleStock")}</span>
            ) : isOnlineRetailer || isWoot ? (
              <>
                <span className="h-[7px] w-[7px] flex-none rounded-full bg-emerald-500" />
                <span className="truncate text-emerald-700 dark:text-emerald-400">{t("products.inStock")}</span>
              </>
            ) : (
              <>
                <span className={`h-[7px] w-[7px] flex-none rounded-full ${latestStock > 0 ? "bg-emerald-500" : "bg-red-500"}`} />
                <span data-deal-guide="stock" className={`truncate ${latestStock > 0 ? "text-emerald-700 dark:text-emerald-400" : "text-gray-500 dark:text-dark-400"}`}>
                  {latestStock > 0 ? samsClubStockLabel(retailer, latestStock, { inStock: t("products.inStock"), lowStock: t("products.lowStock") }) ?? t("card.availCount", { count: latestStock }) : t("products.outOfStock")}
                </span>
              </>
            )}
          </div>
          {product.productKey && (
            <button
              type="button"
              onClick={handleAffiliateClick}
              disabled={affiliateLoading}
              aria-label={retailerLabel}
              title={retailerLabel}
              className="flex h-7 w-7 flex-none items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:text-blue-600 disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-dark-300"
            >
              <ExternalLink size={13} />
            </button>
          )}
          <button
            type="button"
            onClick={handleOpenProductDetailModal}
            aria-label={`${product.name}, ${t("card.viewDetails")}`}
            className="flex h-7 w-7 flex-none items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:text-purple-700 dark:border-white/10 dark:bg-white/5 dark:text-dark-300"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>
    </div>
    {showProductDetailModal &&
      createPortal(
        <div
          className="fixed inset-0 z-[10000] flex items-end justify-center"
          style={{
            backgroundColor: isDetailModalAnimating ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0)",
            transition: "background-color 250ms ease-out",
          }}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) handleCloseProductDetailModal();
          }}
        >
          <div
            className={`bg-white dark:bg-dark-900 w-full h-[75vh] rounded-t-xl overflow-hidden flex flex-col transform transition-all duration-300 ease-out ${
              isDetailModalAnimating ? "translate-y-0" : "translate-y-full"
            }`}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <ProductDetailModal
              sku={getProductDetailIdentifier(product, retailer)}
              storeId={isOnlineRetailer ? "online" : product.storeId}
              retailer={retailer === "walmart_online" ? "walmart" : retailer}
              onClose={handleCloseProductDetailModal}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
});

function TrendingProductsRow({
  t,
  trendingCategories,
  trendingIndex,
  trendingSwipeOffset,
  trendingAnim,
  isTrendingActive,
  onActivate,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onPrev,
  onNext,
}: {
  t: (key: string, params?: Record<string, string | number>) => string;
  trendingCategories: TrendingCategory[];
  trendingIndex: number;
  trendingSwipeOffset: number;
  trendingAnim: "idle" | "exit-left" | "exit-right" | "enter-left" | "enter-right";
  isTrendingActive: boolean;
  onActivate: () => void;
  onTouchStart: (event: TouchEvent<HTMLDivElement>) => void;
  onTouchMove: (event: TouchEvent<HTMLDivElement>) => void;
  onTouchEnd: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const current = trendingCategories[trendingIndex] || trendingCategories[0];
  if (!current) return null;

  return (
    <div className="px-2 pt-1.5 pb-0.5">
      <div
        data-no-swipe
        className={`relative rounded-xl border overflow-hidden cursor-pointer select-none transition-all ${
          isTrendingActive
            ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-700/60 ring-1 ring-emerald-400/30 dark:ring-emerald-600/30"
            : "bg-white dark:bg-dark-900 border-gray-200/60 dark:border-dark-700/60 hover:border-emerald-300/70 dark:hover:border-emerald-700/50 hover:shadow-sm"
        }`}
        onClick={onActivate}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="flex items-center">
          {trendingCategories.length > 1 && (
            <button
              className={`hidden items-center justify-center w-7 flex-shrink-0 h-full hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors ${trendingIndex === 0 ? "md:hidden" : "md:flex"}`}
              onClick={(event) => {
                event.stopPropagation();
                onPrev();
              }}
            >
              <ChevronLeft size={16} className="text-gray-400 dark:text-dark-500" />
            </button>
          )}
          <div
            className="flex-1 min-w-0 px-3 py-2"
            style={{
              transition: trendingSwipeOffset || trendingAnim.startsWith("enter")
                ? "none"
                : "transform 150ms ease-out, opacity 150ms ease-out",
              transform: trendingSwipeOffset
                ? `translateX(${trendingSwipeOffset}px)`
                : trendingAnim === "exit-left"
                ? "translateX(-40px)"
                : trendingAnim === "exit-right"
                ? "translateX(40px)"
                : trendingAnim === "enter-right"
                ? "translateX(40px)"
                : trendingAnim === "enter-left"
                ? "translateX(-40px)"
                : "translateX(0)",
              opacity: trendingAnim === "idle" ? 1 : 0,
            }}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp size={12} className="text-emerald-500 flex-shrink-0" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                {t("products.trendingToday")}
              </span>
              {trendingCategories.length > 1 && (
                <div className="flex items-center gap-0.5 ml-auto">
                  {trendingCategories.map((_, i) => (
                    <div
                      key={i}
                      className={`rounded-full transition-all duration-200 ${i === trendingIndex ? "w-3 h-1 bg-emerald-500 dark:bg-emerald-400" : "w-1 h-1 bg-gray-300 dark:bg-dark-600"}`}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-gray-900 dark:text-dark-50 truncate leading-tight">
                  {categoryLabel(current.category)}
                </p>
                <p className="text-[11px] text-gray-500 dark:text-dark-400 leading-tight mt-0.5">
                  {t("products.newDealsCount", {
                    count: String(current.count),
                    percent: String(current.avgDiscount),
                  })}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {current.topProducts.slice(0, 3).map((product, i) => (
                  <div
                    key={`${product.upc || product.sku || "product"}-${i}`}
                    className="w-9 h-9 rounded-lg overflow-hidden bg-gray-50 dark:bg-dark-800 ring-1 ring-black/[0.05] dark:ring-white/[0.06]"
                  >
                    {product.image ? (
                      <img
                        src={getProductImageUrl(product.image, "100")}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 dark:bg-dark-700" />
                    )}
                  </div>
                ))}
              </div>
              {trendingCategories.length > 1 && trendingIndex === 0 && (
                <div className="md:hidden flex items-center gap-0.5 flex-shrink-0 animate-pulse">
                  <span className="text-[9px] text-gray-400 dark:text-dark-500 whitespace-nowrap">
                    {t("products.swipe")}
                  </span>
                  <ChevronRight size={12} className="text-gray-300 dark:text-dark-600" />
                </div>
              )}
            </div>
          </div>
          {trendingCategories.length > 1 && (
            <button
              className={`hidden items-center justify-center w-7 flex-shrink-0 h-full hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors ${trendingIndex === trendingCategories.length - 1 ? "md:hidden" : "md:flex"}`}
              onClick={(event) => {
                event.stopPropagation();
                onNext();
              }}
            >
              <ChevronRight size={16} className="text-gray-400 dark:text-dark-500" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProductsPage({
  storeId: propStoreId,
  category: propCategory,
}: ProductsPageProps) {
  const navigate = useNavigate();
  const { user, isLoading: userLoading, updateUser } = useUserContext();
  const canUsePennyBeta = hasPennyBetaAccess(user);
  const isFreeUser = isFreeTier(user);
  const [freeAccessMode, setFreeAccessMode] = useState<"online" | "nearby">("online");
  const { t } = useTranslation();
  const retailer = useRetailer();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [dismissedFreeWelcome, setDismissedFreeWelcome] = useState(false);
  const [dismissedReferralInviteForUserId, setDismissedReferralInviteForUserId] = useState<string | null>(null);
  const [savingReferralInviteDismissal, setSavingReferralInviteDismissal] = useState(false);
  const retailerConfig = useRetailerConfig();
  const queryIntent = searchParams.get("intent");
  const queryStoreId = searchParams.get("storeId");
  const queryWelcome = searchParams.get("welcome");
  const querySearchTerm = (searchParams.get("q") || "").slice(0, 120);
  const queryCategory = (searchParams.get("category") || "").slice(0, 80);
  const queryStatus = searchParams.get("status");
  const querySort = searchParams.get("sort");
  const queryResumeIntent = useMemo(
    () => getGuestDealResumeIntent(searchParams),
    [searchParams]
  );
	const closeQueryResumeIntent = useCallback(() => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete("resume");
      next.delete("product");
      return next;
    }, { replace: true });
	}, [setSearchParams]);
	useEffect(() => {
		const upgradePath = getGuestDealUpgradePath(queryResumeIntent);
		if (upgradePath) navigate(upgradePath, { replace: true });
	}, [navigate, queryResumeIntent]);
  const freeIntent = useMemo<FreeUserIntent>(() => {
    if (queryIntent === "shopper" || queryIntent === "reseller" || queryIntent === "both") {
      return queryIntent;
    }
    return getFreeUserIntent();
  }, [queryIntent]);
  const freeIntentCopy = useMemo(() => getFreeIntentCopy(freeIntent), [freeIntent]);
  const showFreeWelcomeCard =
    isFreeUser && queryWelcome === "free" && !dismissedFreeWelcome;

  const finishReferralInviteModal = useCallback(async (openInvites: boolean) => {
    if (savingReferralInviteDismissal) return;
    setSavingReferralInviteDismissal(true);
    try {
      await updateUser({ referralInviteDismissed: true });
      setDismissedReferralInviteForUserId(user?.userId || null);
      if (openInvites) navigate("/settings?tab=referrals");
    } catch (dismissError) {
      console.error("Failed to dismiss referral invitation", dismissError);
      toast.error(t("card.saveChoiceFailed"));
    } finally {
      setSavingReferralInviteDismissal(false);
    }
  }, [navigate, savingReferralInviteDismissal, t, updateUser, user]);
  // Load stores from S3 (skip for online-only retailers like woot, walmart_online)
  const { stores: loadedStores } = useStores<StoreData>(
    retailer === "woot" ||
    retailer === "walmart_online" ||
    retailer === "target_online" ||
    retailer === "amazon" ||
    retailer === "kohls" ||
    isExtraOnlineRetailer(retailer)
      ? "walmart"
      : retailer as RetailerType
  );

  // Debug logging

  // Prevent mobile page scrolling when this component is mounted
  useEffect(() => {
    if (queryIntent === "shopper" || queryIntent === "reseller" || queryIntent === "both") {
      setFreeUserIntent(queryIntent);
    }
  }, [queryIntent]);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, []);

  const queryClient = useQueryClient();
  // Free accounts cannot hide products (server answers an empty list).
  const hiddenProductsQuery = useHiddenProducts(retailerConfig.retailer, isFreeTier(user) ? undefined : user?.userId);
  const hiddenProducts = hiddenProductsQuery.products;
  const changeHiddenProduct = hiddenProductsQuery.change;
  const hiddenProductIds = useMemo(
    () => new Set(hiddenProducts.map((product) => product.productId)),
    [hiddenProducts]
  );
  const [showHiddenProductsModal, setShowHiddenProductsModal] = useState(false);
  const [restoringHiddenProductId, setRestoringHiddenProductId] = useState<string | null>(null);
  const [restoringAllHiddenProducts, setRestoringAllHiddenProducts] = useState(false);
  const [hiddenUndo, setHiddenUndo] = useState<{ product: HiddenProduct; userId: string } | null>(null);
  const dismissHiddenUndo = useCallback(() => setHiddenUndo(null), []);

  useEffect(() => {
    setHiddenUndo(null);
  }, [retailerConfig.retailer, user?.userId]);

  const handleRestoreHiddenProduct = useCallback(async (product: HiddenProduct) => {
    setRestoringHiddenProductId(product.productId);
    try {
      await changeHiddenProduct({ kind: "restore", product });
      setHiddenUndo((current) => current?.product.productId === product.productId ? null : current);
    } catch {
      toast.error(t("products.restoreProductFailed"));
    } finally {
      setRestoringHiddenProductId(null);
    }
  }, [changeHiddenProduct, t]);

  const handleRestoreAllHiddenProducts = useCallback(async () => {
    setRestoringAllHiddenProducts(true);
    try {
      await changeHiddenProduct({ kind: "restoreAll" });
      setHiddenUndo(null);
    } catch {
      toast.error(t("products.restoreProductFailed"));
    } finally {
      setRestoringAllHiddenProducts(false);
    }
  }, [changeHiddenProduct, t]);

  const handleHideProduct = useCallback(async (product: ProductListItem) => {
    const productId = getHiddenProductId(product);
    if (!productId || !user?.userId || hiddenProductIds.has(productId)) return;
    const optimistic: HiddenProduct = {
      productId,
      retailer: retailerConfig.retailer,
      name: product.name,
      image: product.image,
      hiddenAt: Date.now(),
    };
    try {
      const saved = await changeHiddenProduct({ kind: "hide", product: optimistic });
      if (!saved) return;
      setHiddenUndo({ product: saved, userId: user.userId });
    } catch {
      toast.error(t("products.hideProductFailed"));
    }
  }, [hiddenProductIds, changeHiddenProduct, retailerConfig.retailer, t, user?.userId]);
  const initialStoreId = useMemo(
    () => new URLSearchParams(window.location.search).get("storeId") || "all",
    []
  );
  const [selectedStoreId, setSelectedStoreId] = useState<string>(initialStoreId);

  // Home Depot categories hook
  const { data: homeDepotCategories = [] } = useHomeDepotCategories();

  // Refs
  const listContainerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizerRef = useRef<Virtualizer<HTMLDivElement, Element> | null>(null);
  const filteredProductsRef = useRef<ProductListItem[]>([]);
  const userZipCodeRef = useRef<string | undefined>(undefined);
  const suppressScrollAdjustRef = useRef(false);
  const suppressScrollAdjustTimeoutRef = useRef<number | null>(null);
  const pendingRetailerScrollResetRouteRef = useRef<string | null>(null);
  const restoredScrollRouteRef = useRef<string | null>(null);
  const renderedScrollRetailerRef = useRef(retailerConfig.retailer);

  // Initialize scroll position management
  const {
    saveScrollPosition,
    restoreScrollPosition,
    resetScrollPosition,
    scrollToProduct,
  } =
    useScrollPosition(
      parentRef as React.RefObject<HTMLDivElement | null>,
      virtualizerRef
    );

  // State for scroll position and auto-refresh
  const [isAtTop, setIsAtTop] = useState(true);

  // State for swipe hint
  const [showSwipeHint, setShowSwipeHint] = useState(false);

  // State for swipe gesture tracking
  const [isSwiping, setIsSwiping] = useState(false);

  // State for list animation
  const [swipeDirection, setSwipeDirection] = useState<"left" | "right" | null>(
    null
  );
  const [isAnimatingSwipe, setIsAnimatingSwipe] = useState(false);

  // State for live swipe movement
  const [swipeOffset, setSwipeOffset] = useState(0);

  // Product search state
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<StoreProductResult[]>([]);
  const [homeDepotSearchResults, setHomeDepotSearchResults] = useState<
    HomeDepotStoreProductResult[]
  >([]);
  const [searchAmazonData, setSearchAmazonData] = useState<{
    price?: number; title?: string; url?: string; isPrime?: boolean;
  } | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchProductId, setSearchProductId] = useState<string | null>(null);
  const [showNearbyResultsModal, setShowNearbyResultsModal] = useState(false);

  // Zip code modal state
  const [showZipCodeModal, setShowZipCodeModal] = useState(false);
  const [pendingSearch, setPendingSearch] = useState<{
    code: string;
    type: "UPC" | "SKU";
    productId: string;
    purpose: "nearby" | "barcode";
  } | null>(null);

  // Penny items disclaimer modal state
  const [showPennyItemsDisclaimer, setShowPennyItemsDisclaimer] =
    useState(false);
  const [showPennyModeChooser, setShowPennyModeChooser] = useState(false);

  // Bot filters onboarding modal state - shown when user doesn't have stores or categories configured
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [dismissedStoreOnboardingFor, setDismissedStoreOnboardingFor] = useState<string | null>(null);

  // LocalStorage key for penny items disclaimer
  const PENNY_ITEMS_DISCLAIMER_KEY = "dealhawk_penny_items_disclaimer_seen";
  const userStoreIds = useMemo(
    () => getUserStoresForRetailer(user, retailer),
    [user, retailer]
  );
  const initSaved = useMemo(
    () => loadSavedFilters(getFiltersKey(retailerConfig.retailer, initialStoreId)),
    [initialStoreId, retailerConfig.retailer]
  );

  // Every account can open the public online catalogs. Preserve explicit
  // online store assignments for any retailer outside that baseline list.
  const hasOnlineOption = useMemo(() => {
    return (
      hasPublicOnlineFeedAccess(retailerConfig.retailer, true) ||
      userStoreIds.includes("online")
    );
  }, [retailerConfig.retailer, userStoreIds]);

  // Nearby plan "extra stores" (Home Depot and Walmart) for this retailer.
  const extraStores = getExtraStores(user, retailerConfig.retailer);
  // Extra stores the member hid in the store picker: not listed, not in "All".
  const { hiddenIdsFor, hideExtraStore, showExtraStore } = useHiddenExtraStores();
  const hiddenExtraIds = useMemo(
    () => hiddenIdsFor(retailerConfig.retailer),
    [hiddenIdsFor, retailerConfig.retailer]
  );
  const { visible: visibleExtraStores, hidden: hiddenExtraStores } = useMemo(
    () => splitExtraStores(extraStores, hiddenExtraIds),
    [extraStores, hiddenExtraIds]
  );
  const activeHiddenExtraIds = useMemo(
    () => activeHiddenExtraStoreIds(extraStores, hiddenExtraIds),
    [extraStores, hiddenExtraIds]
  );
  const hiddenExtraStoreOptions = useMemo(
    () => appendExtraStoreOptions([], hiddenExtraStores, loadedStores || undefined, retailerConfig.retailer),
    [hiddenExtraStores, loadedStores, retailerConfig.retailer]
  );

  // Get available store filter options ("online" is filtered out — it's a separate toggle)
  const storeFilterOptions = useMemo((): StoreFilterOption[] => {
    const base = buildStoreFilterOptions({
      retailer: retailerConfig.retailer,
      userStoreIds,
      loadedStores: loadedStores || undefined,
    });
    const withExtras = appendExtraStoreOptions(
      base,
      visibleExtraStores,
      loadedStores || undefined,
      retailerConfig.retailer
    );
    if (user?.admin !== true) return withExtras;
    return appendAdminStoreOption(
      withExtras,
      selectedStoreId,
      loadedStores || undefined,
      retailerConfig.retailer
    );
  }, [loadedStores, retailerConfig.retailer, userStoreIds, user?.admin, visibleExtraStores, selectedStoreId]);

  // Amazon/Kohl's and other online-only retailers: keep storeId pinned to "online".
  useEffect(() => {
    if (
      retailerConfig.retailer !== "amazon" &&
      retailerConfig.retailer !== "kohls" &&
      !isExtraOnlineRetailer(retailerConfig.retailer)
    )
      return;
    if (selectedStoreId === "online" && queryStoreId === "online") return;
    setSelectedStoreId("online");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("storeId", "online");
      return next;
    });
  }, [queryStoreId, retailerConfig.retailer, selectedStoreId, setSearchParams]);

  // Free users are online-only: lock storeId to online for all retailers.
  useEffect(() => {
    if (userLoading || !user || !isFreeUser) return;
    if (selectedStoreId === "online" && queryStoreId === "online") return;
    setSelectedStoreId("online");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("storeId", "online");
      return next;
    });
  }, [isFreeUser, queryStoreId, selectedStoreId, setSearchParams, user, userLoading]);

  // Initialize selectedStoreId from query params or default to "all"
  useEffect(() => {
    const availableStoreIds = storeFilterOptions.map((option) => option.id);
    const isAdmin = user?.admin === true;
    const isOnlineMode = queryStoreId === "online";

    if (
      queryStoreId &&
      (isAdmin || isOnlineMode || availableStoreIds.includes(queryStoreId)) &&
      selectedStoreId !== queryStoreId
    ) {
      setSelectedStoreId(queryStoreId);
    } else if (!queryStoreId && selectedStoreId !== "all") {
      setSelectedStoreId("all");
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("storeId", "all");
        return newParams;
      });
    }
  }, [queryStoreId, selectedStoreId, setSearchParams, storeFilterOptions, user?.admin]);

  // Track the previous retailer to only reset when it actually changes
  const prevRetailerRef = useRef<string | null>(null);

  // Load saved filters when retailer actually changes (not just on re-renders)
  useEffect(() => {
    const currentRetailer = retailerConfig.retailer;
    if (prevRetailerRef.current !== null && prevRetailerRef.current !== currentRetailer) {
      const availableStoreIds = storeFilterOptions.map((option) => option.id);
      const isAdmin = user?.admin === true;
      const isOnlineMode = queryStoreId === "online";
      const hasValidQueryStoreId =
        !!queryStoreId &&
        (isAdmin || isOnlineMode || availableStoreIds.includes(queryStoreId));
      const isForcedOnline =
        isFreeUser || currentRetailer === "amazon" || currentRetailer === "kohls" || isExtraOnlineRetailer(currentRetailer);
      const nextStoreId = isForcedOnline
        ? "online"
        : hasValidQueryStoreId && queryStoreId
        ? queryStoreId
        : "all";
      const nextSearchParams = new URLSearchParams(location.search);
      nextSearchParams.set("storeId", nextStoreId);
      const currentRouteKey = `${location.pathname}${location.search}`;
      const nextRouteKey = `${location.pathname}?${nextSearchParams.toString()}`;
      pendingRetailerScrollResetRouteRef.current = nextRouteKey;

      resetScrollPosition();
      // Retailer navigation commonly lands on the bare products route before
      // storeId is normalized below. Clear that destination key too, otherwise
      // the follow-up URL update can restore an old position for the new list.
      if (nextRouteKey !== currentRouteKey) {
        resetScrollPosition(nextRouteKey);
      }

      // The filter-scope effect below swaps in this retailer's saved filters.
      setSelectedStoreId(nextStoreId);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("storeId", nextStoreId);
        return newParams;
      });
    }
    prevRetailerRef.current = currentRetailer;
  }, [
    location.pathname,
    location.search,
    isFreeUser,
    queryStoreId,
    resetScrollPosition,
    retailerConfig.retailer,
    storeFilterOptions,
    user?.admin,
    setSearchParams,
  ]);

  // Initialize filters from localStorage keyed by retailer:store or retailer:online
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
    if (propCategory) return normalizeCategorySelection([propCategory]);
    if (queryCategory) return normalizeCategorySelection([queryCategory]);
    return normalizeCategorySelection(initSaved.selectedCategories);
  });
  const [showNewestOnly, setShowNewestOnly] = useState(() => initSaved.showNewestOnly ?? false);
  const [showVariantsOnly, setShowVariantsOnly] = useState(() => initSaved.showVariantsOnly ?? false);
  const [hideCashback, setHideCashback] = useState(
    () => initSaved.hideCashback ?? DEFAULT_HIDE_CASHBACK
  );
  const [showHotDeals, setShowHotDeals] = useState(false);
  const [showPennyItems, setShowPennyItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState(querySearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(querySearchTerm);
  const [selectedSavedKeywords, setSelectedSavedKeywords] = useState<string[]>(
    () => loadSelectedSavedKeywords()
  );
  const [sortBy, setSortBy] = useState(() =>
    querySort === "discount" ? "discountPercentage" :
    querySort === "price_low" || querySort === "price_high" ? "storePrice" :
    querySort ? "lastUpdated" :
    initSaved.sortBy ?? getDefaultSortBy(retailerConfig.retailer, initialStoreId)
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(() =>
    querySort ? (querySort === "price_low" ? "asc" : "desc") : initSaved.sortDirection ?? "desc"
  );
  const [minStorePrice, setMinStorePrice] = useState<number | undefined>(() => {
    const value = Number(searchParams.get("minPrice"));
    return Number.isFinite(value) && value >= 0 && searchParams.has("minPrice")
      ? value
      : initSaved.minStorePrice;
  });
  const [maxStorePrice, setMaxStorePrice] = useState<number | undefined>(() => {
    const value = Number(searchParams.get("maxPrice"));
    return Number.isFinite(value) && value >= 0 && searchParams.has("maxPrice")
      ? value
      : initSaved.maxStorePrice;
  });
  const [minMsrp, setMinMsrp] = useState<number | undefined>(() => initSaved.minMsrp);
  const [maxMsrp, setMaxMsrp] = useState<number | undefined>(() => initSaved.maxMsrp);
  const [minDiscount, setMinDiscount] = useState<number | undefined>(() => {
    const value = Number(searchParams.get("discount"));
    return Number.isFinite(value) && value >= 0 && searchParams.has("discount")
      ? value
      : initSaved.minDiscount;
  });
  const [showInStockOnly, setShowInStockOnly] = useState(() => initSaved.showInStockOnly ?? true);
  const [isPending, startTransition] = useTransition();
  // `pendingDealViewMode` updates synchronously to give the tab indicator
  // immediate visual feedback. `dealViewMode` is updated inside a transition
  // so the heavier content swap (TopProductsView <-> products list) doesn't
  // block the tap response.
  // A deep-linked search (?q=) lands on the All feed, where the search row lives.
  const [pendingDealViewMode, setPendingDealViewMode] = useState<DealViewMode>(() =>
    querySearchTerm.trim() ? "all" : loadDealViewMode()
  );
  const [dealViewMode, setDealViewMode] = useState<DealViewMode>(pendingDealViewMode);
  useEffect(() => {
    try { localStorage.setItem(PRODUCTS_DEAL_VIEW_MODE_KEY, dealViewMode); } catch {
      /* localStorage unavailable */
    }
  }, [dealViewMode]);
  const handleDealViewModeChange = useCallback((mode: DealViewMode) => {
    setPendingDealViewMode(mode);
    startTransition(() => setDealViewMode(mode));
  }, []);
  // List or grid, remembered per tab: the All feed defaults to list, Top to grid.
  const [allLayout, setAllLayout] = useLayoutMode("all");
  const [topLayout, setTopLayout] = useLayoutMode("top");
  const viewMode: "list" | "gallery" = allLayout === "grid" ? "gallery" : "list";
  const isGallery = viewMode === "gallery";
  const headerViewMode: "list" | "gallery" = (pendingDealViewMode === "top" ? topLayout : allLayout) === "grid" ? "gallery" : "list";
  const handleViewModeChange = useCallback((mode: "list" | "gallery") => {
    const next = mode === "gallery" ? "grid" : "list";
    startTransition(() => (pendingDealViewMode === "top" ? setTopLayout(next) : setAllLayout(next)));
  }, [pendingDealViewMode, setAllLayout, setTopLayout]);
  const [galleryViewportWidth, setGalleryViewportWidth] = useState(0);


  // State for product status filter (All, New, Price Drop, Stock Update, With Location, Saved, Pickup)
  const [productStatusFilter, setProductStatusFilter] = useState<
    StatusValue
  >(
    queryStatus === "new" || queryStatus === "price_drop" || queryStatus === "lowest" || queryStatus === "cashback" || queryStatus === "ibotta" || queryStatus === "bogo" || queryStatus === "mix_match" || queryStatus === "coupon"
      ? queryStatus
      : "all"
  );
  const savedKeywords = useMemo(
    () => sanitizeSavedKeywords(user?.searchTerms),
    [user?.searchTerms]
  );
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!value.trim()) { setDebouncedSearchTerm(""); return; }
    searchDebounceRef.current = setTimeout(() => setDebouncedSearchTerm(value), 150);
  }, []);
  const clearSearch = useCallback(() => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);
  const searchTokens = useMemo(
    () => buildSearchTokens(debouncedSearchTerm, selectedSavedKeywords),
    [debouncedSearchTerm, selectedSavedKeywords]
  );

  useEffect(() => {
    if (userLoading) return;
    setSelectedSavedKeywords((prev) =>
      prev.filter((keyword) =>
        savedKeywords.some(
          (savedKeyword) => savedKeyword.toLowerCase() === keyword.toLowerCase()
        )
      )
    );
  }, [savedKeywords, userLoading]);

  useEffect(() => {
    saveSelectedSavedKeywords(selectedSavedKeywords);
  }, [selectedSavedKeywords]);

  // Filters are saved/loaded per retailer:store and retailer:online scope.
  // loadedFiltersKey names the scope the current filter values belong to; it
  // changes in the same render as the values, so a retailer or store-type
  // switch can never write one scope's filters under another scope's key.
  const filtersKey = getFiltersKey(retailerConfig.retailer, selectedStoreId);
  const [loadedFiltersKey, setLoadedFiltersKey] = useState(() =>
    getFiltersKey(retailerConfig.retailer, initialStoreId)
  );

  useEffect(() => {
    saveFiltersToStorage(loadedFiltersKey, {
      selectedCategories, minStorePrice, maxStorePrice, minMsrp, maxMsrp, minDiscount, showNewestOnly, showInStockOnly, showVariantsOnly, hideCashback, sortBy, sortDirection,
    });
  }, [loadedFiltersKey, selectedCategories, minStorePrice, maxStorePrice, minMsrp, maxMsrp, minDiscount, showNewestOnly, showInStockOnly, showVariantsOnly, hideCashback, sortBy, sortDirection]);

  // Switching retailer or store type (store <-> online) loads that scope's filters
  // and drops the previous scope's search, status, and deal toggles.
  useEffect(() => {
    if (filtersKey === loadedFiltersKey) return;
    const saved = loadSavedFilters(filtersKey);
    setLoadedFiltersKey(filtersKey);
    setSelectedCategories(normalizeCategorySelection(saved.selectedCategories));
    setMinStorePrice(saved.minStorePrice);
    setMaxStorePrice(saved.maxStorePrice);
    setMinMsrp(saved.minMsrp);
    setMaxMsrp(saved.maxMsrp);
    setMinDiscount(saved.minDiscount);
    setShowNewestOnly(saved.showNewestOnly ?? false);
    setShowInStockOnly(saved.showInStockOnly ?? true);
    setShowVariantsOnly(saved.showVariantsOnly ?? false);
    setHideCashback(saved.hideCashback ?? DEFAULT_HIDE_CASHBACK);
    setSortBy(saved.sortBy ?? getDefaultSortBy(retailerConfig.retailer, selectedStoreId));
    setSortDirection(saved.sortDirection ?? "desc");
    setShowHotDeals(false);
    setShowPennyItems(false);
    clearSearch();
    setProductStatusFilter("all");
  }, [filtersKey, loadedFiltersKey, clearSearch, retailerConfig.retailer, selectedStoreId]);

  useEffect(() => {
    if (!supportsCrazyDeals(retailerConfig.retailer) && showHotDeals) {
      setShowHotDeals(false);
    }

    if (!supportsPennyItems(retailerConfig.retailer) && showPennyItems) {
      setShowPennyItems(false);
    }
  }, [retailerConfig.retailer, showHotDeals, showPennyItems]);

  // Fetch retailer-scoped favorites for paid accounts only. Free accounts can
  // browse online catalogs but have no saved-products surface.
  const favoritesRetailer = retailerConfig.retailer;
  const { favorites: rawSavedProducts, refetch: refetchFavorites, isLoading: favoritesLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useFlatFavoritesWithRetailer(
    500, // Large page size to minimize pagination
    favoritesRetailer,
    false,
    {
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      allowFreeOnline: selectedStoreId === "online",
      enabled: !isFreeUser,
    }
  );
  const savedProducts = rawSavedProducts;
  const removeFavoriteMutation = useRemoveFavoriteWithRetailer(favoritesRetailer, false, true);

  // Auto-fetch remaining favorites pages if there are more.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const apiStoreId = getApiStoreId({
    retailer: retailerConfig.retailer,
    selectedStoreId,
    isAdmin: user?.admin === true,
  });
  // Top aggregates physical stores; All/Saved retains its selected store scope.
  // Online selections stay online in both views, regardless of retailer plan.
  const topApiStoreId = selectedStoreId === "online" ? apiStoreId : undefined;

  const isWalmartOnlineMode =
    retailerConfig.retailer === "walmart" && selectedStoreId === "online";
  // Check if this is an online-only mode (no physical store handling needed)
  const isOnlineRetailer =
    isOnlineRetailerRetailer(retailerConfig.retailer) || selectedStoreId === "online";
  const showDealAccessSwitcher = supportsOnlineAndNearbyModes(retailerConfig.retailer);
  const showFreeNearbyPreview =
    isFreeUser &&
    retailerConfig.retailer === "walmart" &&
    showDealAccessSwitcher &&
    freeAccessMode === "nearby";
  const showFreeNearbyTeaser =
    isFreeUser &&
    retailerConfig.retailer === "walmart" &&
    showDealAccessSwitcher &&
    selectedStoreId === "online" &&
    freeAccessMode === "online";
  useEffect(() => {
    if (!isFreeUser || retailerConfig.retailer !== "walmart" || !showDealAccessSwitcher) {
      setFreeAccessMode("online");
    }
  }, [isFreeUser, retailerConfig.retailer, showDealAccessSwitcher]);

  useEffect(() => {
    if (!isOnlineRetailer || sortBy !== "inventory") return;
    setSortBy("lastUpdated");
    setSortDirection("desc");
  }, [isOnlineRetailer, sortBy]);

  // Each retailer/mode scope keeps its own sort (saved with the filters above);
  // a scope with no saved sort starts on getDefaultSortBy.

  // Truly online-only retailers that use a separate API endpoint
  const usesOnlineApi =
    retailerConfig.retailer === "woot" ||
    retailerConfig.retailer === "walmart_online" ||
    retailerConfig.retailer === "target_online" ||
    isWalmartOnlineMode;

  const needsClientAllProducts = productStatusFilter === "saved" || retailerConfig.retailer === "woot";
  const browseEnabled =
    !!user &&
    dealViewMode === "all" &&
    retailerConfig.retailer !== "woot" &&
    productStatusFilter !== "saved";

  const browseQuery = useInfiniteQuery({
    queryKey: [
      "products-browse",
      retailerConfig.retailer,
      user?.userId,
      selectedStoreId,
      showHotDeals,
      showPennyItems,
      retailerConfig.retailer === "dollar_general" &&
        (productStatusFilter === "coupon" ||
          productStatusFilter === "mix_match" ||
          productStatusFilter === "bogo" ||
          productStatusFilter === "all"),
      searchTokens,
      savedKeywords,
      selectedCategories,
      showNewestOnly,
      showVariantsOnly,
      hideCashback,
      minStorePrice,
      maxStorePrice,
      minMsrp,
      maxMsrp,
      minDiscount,
      showInStockOnly,
      productStatusFilter,
      sortBy,
      sortDirection,
    ],
    queryFn: async ({ pageParam }) => {
      const request = {
        retailer: retailerConfig.retailer,
        storeId: selectedStoreId,
        showHotDeals,
        showPennyItems,
        searchTokens,
        savedKeywords,
        categories: selectedCategories,
        showNewestOnly,
        showVariantsOnly,
        hideCashback,
        minStorePrice,
        maxStorePrice,
        minMsrp,
        maxMsrp,
        minDiscount,
        showInStockOnly,
        status: productStatusFilter === "coupon" ? "all" : productStatusFilter,
        sortBy,
        sortDirection,
        limit: 40,
      };
      const loadCouponFeed = shouldLoadDollarGeneralCouponFeed({
        retailer: retailerConfig.retailer,
        showHotDeals,
        showPennyItems,
        status: productStatusFilter,
      });
      if (!loadCouponFeed) {
        return browseProducts({
          ...request,
          showCoupons:
            retailerConfig.retailer === "dollar_general" &&
            (productStatusFilter === "coupon" ||
              productStatusFilter === "mix_match" ||
              productStatusFilter === "bogo"),
          cursor: pageParam,
        });
      }
      const cursor = parseDollarGeneralCouponCursor(pageParam);
      const [main, coupons] = await Promise.all([
        cursor.mainDone
          ? emptyProductBrowsePage()
          : browseProducts({ ...request, showCoupons: false, cursor: cursor.main }),
        cursor.couponDone
          ? emptyProductBrowsePage()
          : browseProducts({ ...request, showCoupons: true, cursor: cursor.coupon }),
      ]);
      return mergeDollarGeneralBrowseResponses(main, coupons);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage?.hasMore ? lastPage.nextCursor : undefined,
    // Load the first All Deals page for its authoritative total while Top is open.
    // Further pages are only requested by scrolling the All Deals list.
    enabled: !!user && !needsClientAllProducts && (browseEnabled || !isFreeUser),
    // Keep the feed and category grid in place while filters fetch new results.
    // Never carry another account, retailer, or store's data into this view.
    placeholderData: (previousData, previousQuery) => {
      const [feed, retailer, userId, storeId] = previousQuery?.queryKey ?? [];
      return feed === "products-browse" &&
        retailer === retailerConfig.retailer &&
        userId === user?.userId &&
        storeId === selectedStoreId
        ? previousData
        : undefined;
    },
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // The legacy complete response remains for Top Deals, online adapters, and
  // Saved mode while those specialized views are migrated separately.
  const legacyQueryOptions = (storeId: string | undefined) => ({
    queryKey: [...(usesOnlineApi
      ? retailerConfig.retailer === "woot"
        ? ["woot-products"]
        : retailerConfig.retailer === "target_online"
        ? getOnlineProductsQueryKey("TARGET")
        : getOnlineProductsQueryKey("WALMART")
      : getProductsV2QueryKey(
          retailerConfig.retailer,
          showHotDeals,
          showPennyItems,
          storeId
        )), user?.userId],
    queryFn: async () => {
      if (retailerConfig.retailer === "woot") {
        const wootProducts = await getRecentWootProducts();
        if (!wootProducts || wootProducts.length === 0) return { products: [], totalResults: 0 };
        const products = buildWootProducts(wootProducts);
        return { products, totalResults: products.length };
      }
      if (retailerConfig.retailer === "walmart_online" || isWalmartOnlineMode) {
        const response = await getOnlineProducts({ retailer: "WALMART", limit: 100 });
        if (!response.products || response.products.length === 0) return { products: [], totalResults: 0 };
        return {
          products: buildWalmartOnlineProducts(response.products),
          totalResults: response.totalResults,
        };
      }
      if (retailerConfig.retailer === "target_online") {
        const response = await getOnlineProducts({ retailer: "TARGET", limit: 100 });
        if (!response.products || response.products.length === 0) return { products: [], totalResults: 0 };
        return {
          products: buildTargetOnlineProducts(response.products),
          totalResults: response.totalResults,
        };
      }
      return getProductsV2({
        retailer: retailerConfig.retailer,
        showHotDeals,
        showPennyItems,
        storeId,
      });
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const legacyProductsQuery = useQuery({
    ...legacyQueryOptions(apiStoreId),
    enabled: !!user && needsClientAllProducts,
  });
  // Subscribe to Top's stable store scope independently of the active All Deals
  // query. Matching legacy/Top keys share one request and one cached response.
  const topProductsQuery = useQuery({
    ...legacyQueryOptions(topApiStoreId),
    enabled: !!user && dealViewMode === "top",
  });
  const communityPreviewQuery = useQuery({
    queryKey: getCommunitySuccessesQueryKey(3),
    queryFn: () => getCommunitySuccesses(3),
    enabled: Boolean(user?.userId) && isFreeUser && dealViewMode === "top",
    staleTime: 2 * 60 * 1000,
  });

  const browseData = useMemo(() => {
    if (!browseQuery.data?.pages.length) return undefined;
    const pages = browseQuery.data.pages.filter(
      (page): page is ProductBrowseResponse => Boolean(page)
    );
    if (!pages.length) return undefined;
    const seen = new Set<string>();
    const products = pages
      .flatMap((page) => page.products)
      .filter((product) => {
        const key = product.upc || product.sku;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    return {
      products,
      totalResults: pages[0].total,
    };
  }, [browseQuery.data]);
  const allProductsData = browseEnabled ? browseData
    : needsClientAllProducts ? legacyProductsQuery.data : topProductsQuery.data;
  const activeProductsQuery = dealViewMode === "top" ? topProductsQuery
    : browseEnabled ? browseQuery : legacyProductsQuery;
  const isLoading = activeProductsQuery.isLoading ||
    Boolean(user?.userId && hiddenProductsQuery.isPending);
  const error = activeProductsQuery.error ||
    (hiddenProductsQuery.data === undefined ? hiddenProductsQuery.error : null);
  const isFetching = activeProductsQuery.isFetching;
  const browseMetadata = browseEnabled
    ? browseQuery.data?.pages.find(Boolean)
    : undefined;
  const browseTotal = browseMetadata?.total;
  const browseStoreCounts = useMemo(() => {
    if (!browseEnabled || !browseQuery.data?.pages.length) return undefined;
    return Object.assign(
      {},
      ...browseQuery.data.pages.filter(Boolean).map((page) => page.storeCounts || {})
    ) as Record<string, number>;
  }, [browseEnabled, browseQuery.data]);

  const {
    hasNextPage: browseHasNextPage,
    isFetchingNextPage: isFetchingNextBrowsePage,
    isPlaceholderData: isShowingPreviousBrowseData,
    fetchNextPage: fetchNextBrowsePage,
  } = browseQuery;
  const handleBrowseScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    if (!browseEnabled || !browseHasNextPage || isFetchingNextBrowsePage || isShowingPreviousBrowseData) return;
    const element = event.currentTarget;
    if (element.scrollHeight - element.scrollTop - element.clientHeight < 900) {
      void fetchNextBrowsePage();
    }
  }, [browseEnabled, browseHasNextPage, isFetchingNextBrowsePage, isShowingPreviousBrowseData, fetchNextBrowsePage]);

  const productsData = useMemo(() => {
    if (!allProductsData?.products) return allProductsData;
    if (user?.userId && hiddenProductsQuery.data === undefined) {
      return { ...allProductsData, products: [] };
    }
    const filtered = getCachedProductsPageComputation(
      allProductsData.products,
      makeProductsPageCacheKey("priced-products", allProductsData.totalResults),
      () =>
        allProductsData.products.filter((p) => {
          const price = p.storePrice?.[p.storePrice.length - 1] ?? 0;
          return price > 0;
        })
    );
    return {
      ...allProductsData,
      products: excludeHiddenExtraStoreProducts(
        filtered.filter((product) => !isProductHidden(product, hiddenProductIds)),
        activeHiddenExtraIds
      ),
    };
  }, [allProductsData, hiddenProductIds, hiddenProductsQuery.data, user?.userId, activeHiddenExtraIds]);

  const hiddenOnlineProductsCount = useMemo(() => {
    if (selectedStoreId !== "online") return 0;
    if (typeof productsData?.totalResults !== "number") return 0;
    // Personal hides are not locked deals. Keep the upgrade-preview count
    // based on the priced response before applying hidden preferences.
    const visibleProductsCount = allProductsData?.products?.filter(
      (product) => (product.storePrice?.[product.storePrice.length - 1] ?? 0) > 0
    ).length || 0;
    return Math.max(productsData.totalResults - visibleProductsCount, 0);
  }, [allProductsData?.products, productsData?.totalResults, selectedStoreId]);
  const retailerLabel = getRetailerLabel(retailerConfig.retailer);

  // True when the user is viewing the curated "Top deals" gallery instead of the
  // full filtered list. Most heavy memos short-circuit so we don't pay the cost
  // of running filters/sort/category aggregation for hidden chrome.
  const isTopMode = dealViewMode === "top" && !isLoading && !error;

  const visibleTopProducts = useMemo(
    () => user?.userId && hiddenProductsQuery.data === undefined ? []
      : excludeHiddenExtraStoreProducts((topProductsQuery.data?.products || []).filter((product) =>
          (product.storePrice?.[product.storePrice.length - 1] ?? 0) > 0 &&
          !isProductHidden(product, hiddenProductIds)), activeHiddenExtraIds),
    [topProductsQuery.data, hiddenProductIds, hiddenProductsQuery.data, user?.userId, activeHiddenExtraIds]
  );
  const topProductsModel = useMemo(
    () => buildTopProductsViewModel(visibleTopProducts, TOP_PRODUCTS_WINDOW_HOURS),
    [visibleTopProducts]
  );

  // Common filtering logic (Category, Search, Price, Stock)
  // This is used as a base for both the display list and the grouping map
  const commonFilteredProducts = useMemo(() => {
    if (isTopMode && !needsClientAllProducts) return [];
    const perfStart = performance.now();
    if (!productsData?.products || !Array.isArray(productsData.products)) {
      return [];
    }
    if (browseEnabled) return productsData.products;
    const nextProducts = getCachedProductsPageComputation(
      productsData.products,
      makeProductsPageCacheKey(
        "common-filtered",
        selectedCategories.join(","),
        showNewestOnly,
        showVariantsOnly,
        hideCashback,
        debouncedSearchTerm,
        searchTokens.join(","),
        minStorePrice,
        maxStorePrice,
        minMsrp,
        maxMsrp,
        minDiscount,
        showInStockOnly,
        retailerConfig.retailer,
        isOnlineRetailer
      ),
      () =>
        applyBaseFilters({
          products: productsData.products,
          selectedCategories,
          showNewestOnly,
          searchTerm: debouncedSearchTerm,
          searchTokens,
          minStorePrice,
          maxStorePrice,
          minMsrp,
          maxMsrp,
          minDiscount,
          showInStockOnly,
          showVariantsOnly,
          hideCashback,
          retailer: retailerConfig.retailer,
          isOnlineRetailer,
        })
    );
    logProductsPagePerf("applyBaseFilters", perfStart, {
      retailer: retailerConfig.retailer,
      totalProducts: productsData.products.length,
      filteredProducts: nextProducts.length,
      keywordCount: searchTokens.length,
    });
    return nextProducts.filter((product) => !isProductHidden(product, hiddenProductIds));
  }, [
    hiddenProductIds,
    productsData?.products,
    selectedCategories,
    showNewestOnly,
    debouncedSearchTerm,
    searchTokens,
    minStorePrice,
    maxStorePrice,
    minMsrp,
    maxMsrp,
    minDiscount,
    showInStockOnly,
    showVariantsOnly,
    hideCashback,
    isOnlineRetailer,
    retailerConfig.retailer,
    isTopMode,
    needsClientAllProducts,
    browseEnabled,
  ]);

  // Convert favorites to ProductListItem format for display in "Saved" mode
  const favoritesAsProducts = useMemo((): ProductListItem[] => {
    if (!savedProducts?.length) return [];
    return buildFavoritesAsProducts(savedProducts);
  }, [savedProducts]);

  // Calculate product status counts (before status filter is applied)
  const productStatusCounts = useMemo(() => {
    if (browseEnabled && browseMetadata) {
      return {
        ...browseMetadata.statusCounts,
        saved: savedProducts?.length || 0,
      };
    }
    if (isTopMode) {
      return {
        all: 0, new: 0, priceDrop: 0, openBox: 0, stockUpdate: 0,
        withLocation: 0, withOptions: 0, saved: savedProducts?.length || 0,
        pickupAvailable: 0, pickupUnavailable: 0, onSale: 0,
        soldByWalmart: 0, soldByProSeller: 0, lowest: 0, cashback: 0, bogo: 0, mixMatch: 0, coupon: 0,
      };
    }
    const perfStart = performance.now();
    if (!productsData?.products || !Array.isArray(productsData.products)) {
      return {
        all: 0,
        new: 0,
        priceDrop: 0,
        openBox: 0,
        stockUpdate: 0,
        withLocation: 0,
        withOptions: 0,
        saved: savedProducts?.length || 0,
        pickupAvailable: 0,
        pickupUnavailable: 0,
        onSale: 0,
        soldByWalmart: 0,
        soldByProSeller: 0,
        lowest: 0,
        cashback: 0,
        bogo: 0,
        mixMatch: 0,
        coupon: 0,
      };
    }
    const counts = getCachedProductsPageComputation(
      commonFilteredProducts,
      makeProductsPageCacheKey(
        "status-counts",
        selectedStoreId,
        isOnlineRetailer,
        retailerConfig.retailer,
        getProductsPageCacheId(savedProducts),
        getProductsPageCacheId(productsData.products)
      ),
      () =>
        computeProductStatusCountsFromBaseProducts({
          products: commonFilteredProducts,
          selectedStoreId,
          isOnlineRetailer,
          retailer: retailerConfig.retailer,
          savedProducts,
          allProducts: productsData.products,
        })
    );
    logProductsPagePerf("computeStatusCounts", perfStart, {
      retailer: retailerConfig.retailer,
      candidateProducts: commonFilteredProducts.length,
      scopedProducts: counts.all,
      selectedStoreId,
    });
    return counts;
  }, [
    commonFilteredProducts,
    productsData?.products,
    savedProducts,
    selectedStoreId,
    isOnlineRetailer,
    retailerConfig.retailer,
    isTopMode,
    browseEnabled,
    browseMetadata,
  ]);

  const keywordBaseProducts = useMemo(() => {
    if (isTopMode) return [];
    if (!productsData?.products || !Array.isArray(productsData.products)) {
      return [];
    }
    return getCachedProductsPageComputation(
      productsData.products,
      makeProductsPageCacheKey(
        "keyword-base",
        getProductsPageCacheId(favoritesAsProducts),
        selectedCategories.join(","),
        showNewestOnly,
        showVariantsOnly,
        hideCashback,
        minStorePrice,
        maxStorePrice,
        minMsrp,
        maxMsrp,
        minDiscount,
        showInStockOnly,
        retailerConfig.retailer,
        isOnlineRetailer,
        productStatusFilter,
        sortBy,
        sortDirection,
        selectedStoreId
      ),
      () => {
        const baseProductsWithoutKeywordSearch = applyBaseFilters({
          products: productsData.products,
          selectedCategories,
          showNewestOnly,
          showVariantsOnly,
          hideCashback,
          searchTerm: "",
          searchTokens: [],
          minStorePrice,
          maxStorePrice,
          minMsrp,
          maxMsrp,
          minDiscount,
          showInStockOnly,
          retailer: retailerConfig.retailer,
          isOnlineRetailer,
        });
        return filterAndSortProducts({
          favoritesAsProducts,
          commonFilteredProducts: baseProductsWithoutKeywordSearch,
          productStatusFilter,
          sortBy,
          sortDirection,
          searchTerm: "",
          searchTokens: [],
          selectedStoreId,
          showVariantsOnly,
          hideCashback,
          allProducts: productsData.products,
          retailer: retailerConfig.retailer,
          isOnlineRetailer,
        });
      }
    );
  }, [
    productsData?.products,
    favoritesAsProducts,
    selectedCategories,
    showNewestOnly,
    showVariantsOnly,
    hideCashback,
    minStorePrice,
    maxStorePrice,
    minMsrp,
    maxMsrp,
    minDiscount,
    showInStockOnly,
    retailerConfig.retailer,
    isOnlineRetailer,
    productStatusFilter,
    sortBy,
    sortDirection,
    selectedStoreId,
    isTopMode,
  ]);

  const keywordMatchCounts = useMemo(() => {
    if (browseEnabled && browseMetadata) return browseMetadata.keywordCounts;
    if (!savedKeywords.length) return {};
    return getCachedProductsPageComputation(
      keywordBaseProducts,
      makeProductsPageCacheKey("keyword-counts", savedKeywords.join("\n")),
      () => {
        const haystacks = keywordBaseProducts.map(getProductSearchHaystack);
        return savedKeywords.reduce<Record<string, number>>((acc, keyword) => {
          const normalizedKeyword = keyword.trim().toLowerCase();
          acc[keyword] = normalizedKeyword
            ? haystacks.reduce(
                (count, haystack) =>
                  count + (haystack.includes(normalizedKeyword) ? 1 : 0),
                0
              )
            : 0;
          return acc;
        }, {});
      }
    );
  }, [keywordBaseProducts, savedKeywords, browseEnabled, browseMetadata]);

  // Client-side filtering, searching, and sorting logic
  const filteredProducts = useMemo(() => {
    if (isTopMode && !needsClientAllProducts) return [];
    if (browseEnabled) {
      return (productsData?.products || []).filter(
        (product) => !isProductHidden(product, hiddenProductIds)
      );
    }
    const perfStart = performance.now();
    const cacheScope =
      productStatusFilter === "saved" ? favoritesAsProducts : commonFilteredProducts;
    const nextProducts = getCachedProductsPageComputation(
      cacheScope,
      makeProductsPageCacheKey(
        "filtered-products",
        getProductsPageCacheId(commonFilteredProducts),
        getProductsPageCacheId(productsData?.products),
        productStatusFilter,
        sortBy,
        sortDirection,
        debouncedSearchTerm,
        searchTokens.join(","),
        selectedStoreId,
        showVariantsOnly,
        hideCashback,
        retailerConfig.retailer,
        isOnlineRetailer
      ),
      () =>
        filterAndSortProducts({
          favoritesAsProducts,
          commonFilteredProducts,
          productStatusFilter,
          sortBy,
          sortDirection,
          searchTerm: debouncedSearchTerm,
          searchTokens,
          selectedStoreId,
          showVariantsOnly,
          hideCashback,
          allProducts: productsData?.products,
          retailer: retailerConfig.retailer,
          isOnlineRetailer,
        })
    );
    logProductsPagePerf("filterAndSortProducts", perfStart, {
      retailer: retailerConfig.retailer,
      productStatusFilter,
      selectedStoreId,
      totalProducts: commonFilteredProducts.length,
      filteredProducts: nextProducts.length,
      sortBy,
      sortDirection,
    });
    return nextProducts.filter((product) => !isProductHidden(product, hiddenProductIds));
  }, [
    commonFilteredProducts,
    selectedStoreId,
    sortBy,
    sortDirection,
    productStatusFilter,
    favoritesAsProducts,
    debouncedSearchTerm,
    searchTokens,
    productsData?.products,
    showVariantsOnly,
    hideCashback,
    isOnlineRetailer,
    retailerConfig.retailer,
    isTopMode,
    needsClientAllProducts,
    browseEnabled,
    hiddenProductIds,
  ]);

  // Never substitute loaded page lengths or results from previous filters.
  const countsReady = !user?.userId || hiddenProductsQuery.data !== undefined;
  const topProductsCount = countsReady && topProductsQuery.data !== undefined
    ? topProductsModel.count : undefined;
  const allProductsCount = !countsReady ? undefined
    : productStatusFilter === "saved"
      ? (!favoritesLoading && !hasNextPage && !isFetchingNextPage &&
          (selectedStoreId === "all" || legacyProductsQuery.data !== undefined) ? filteredProducts.length : undefined)
      : retailerConfig.retailer === "woot"
        ? (legacyProductsQuery.data !== undefined ? filteredProducts.length : undefined)
        : browseQuery.isPlaceholderData ? undefined : browseQuery.data?.pages[0]?.total;

  // Tab counts: Top / All keep their own numbers while Crazy or Penny deals is on, and the
  // deep-discount tabs show a live count of their own. Limit-1 browse probes supply the
  // totals the main feed is not currently loading.
  const countProbeFilters = [
    searchTokens,
    savedKeywords,
    selectedCategories,
    showNewestOnly,
    showVariantsOnly,
    hideCashback,
    minStorePrice,
    maxStorePrice,
    minMsrp,
    maxMsrp,
    minDiscount,
    showInStockOnly,
    productStatusFilter,
  ] as const;
  const countProbesEnabled =
    !!user &&
    !needsClientAllProducts &&
    !isFreeUser &&
    !shouldLoadDollarGeneralCouponFeed({
      retailer: retailerConfig.retailer,
      showHotDeals: false,
      showPennyItems: false,
      status: productStatusFilter,
    });
  const fetchBrowseTotal = useCallback(
    async (flags: { showHotDeals: boolean; showPennyItems: boolean }) => {
      const page = await browseProducts({
        retailer: retailerConfig.retailer,
        storeId: selectedStoreId,
        showHotDeals: flags.showHotDeals,
        showPennyItems: flags.showPennyItems,
        searchTokens,
        savedKeywords,
        categories: selectedCategories,
        showNewestOnly,
        showVariantsOnly,
        hideCashback,
        minStorePrice,
        maxStorePrice,
        minMsrp,
        maxMsrp,
        minDiscount,
        showInStockOnly,
        status: productStatusFilter === "coupon" ? "all" : productStatusFilter,
        sortBy,
        sortDirection,
        limit: 1,
        showCoupons:
          retailerConfig.retailer === "dollar_general" &&
          (productStatusFilter === "coupon" ||
            productStatusFilter === "mix_match" ||
            productStatusFilter === "bogo"),
      });
      return page?.total;
    },
    [
      retailerConfig.retailer,
      selectedStoreId,
      searchTokens,
      savedKeywords,
      selectedCategories,
      showNewestOnly,
      showVariantsOnly,
      hideCashback,
      minStorePrice,
      maxStorePrice,
      minMsrp,
      maxMsrp,
      minDiscount,
      showInStockOnly,
      productStatusFilter,
      sortBy,
      sortDirection,
    ]
  );
  const deepDiscountOn = showHotDeals || showPennyItems;
  const baseAllTotalQuery = useQuery({
    queryKey: ["products-browse-count", "all", retailerConfig.retailer, user?.userId, selectedStoreId, ...countProbeFilters],
    queryFn: () => fetchBrowseTotal({ showHotDeals: false, showPennyItems: false }),
    enabled: countProbesEnabled && deepDiscountOn,
    staleTime: 60_000,
  });
  const crazyDealsTotalQuery = useQuery({
    queryKey: ["products-browse-count", "crazy", retailerConfig.retailer, user?.userId, selectedStoreId, ...countProbeFilters],
    queryFn: () => fetchBrowseTotal({ showHotDeals: true, showPennyItems: false }),
    enabled: countProbesEnabled && supportsCrazyDeals(retailerConfig.retailer) && !showHotDeals,
    staleTime: 60_000,
  });
  const pennyDealsTotalQuery = useQuery({
    queryKey: ["products-browse-count", "penny", retailerConfig.retailer, user?.userId, selectedStoreId, ...countProbeFilters],
    queryFn: () => fetchBrowseTotal({ showHotDeals: false, showPennyItems: true }),
    enabled:
      countProbesEnabled &&
      supportsPennyItems(retailerConfig.retailer) &&
      retailerConfig.retailer !== "home_depot" &&
      !showPennyItems,
    staleTime: 60_000,
  });
  const heldCounts = useHeldCounts(
    {
      top: topProductsCount,
      all: deepDiscountOn ? baseAllTotalQuery.data : allProductsCount,
      crazy: showHotDeals ? allProductsCount : crazyDealsTotalQuery.data,
      penny: showPennyItems ? allProductsCount : pennyDealsTotalQuery.data,
    },
    [retailerConfig.retailer, user?.userId, selectedStoreId, ...countProbeFilters]
  );


  const trendingCategories = useMemo(() => {
    if (isTopMode) return [];
    if (!productsData?.products?.length) return [];
    return getCachedProductsPageComputation(
      productsData.products,
      makeProductsPageCacheKey("trending-categories"),
      () => computeTrendingCategories(productsData.products)
    );
  }, [productsData?.products, isTopMode]);

  const [trendingIndex, setTrendingIndex] = useState(0);
  const trendingTouchRef = useRef<{ startX: number; startY: number; startTime: number } | null>(null);
  const [trendingSwipeOffset, setTrendingSwipeOffset] = useState(0);
  const [trendingAnim, setTrendingAnim] = useState<"idle" | "exit-left" | "exit-right" | "enter-left" | "enter-right">("idle");

  const goTrending = useCallback((next: number) => {
    const goingForward = next > trendingIndex;
    setTrendingAnim(goingForward ? "exit-left" : "exit-right");
    setTimeout(() => {
      setTrendingIndex(next);
      setTrendingAnim(goingForward ? "enter-right" : "enter-left");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTrendingAnim("idle"));
      });
    }, 150);
  }, [trendingIndex]);

  useEffect(() => {
    setTrendingIndex(0);
    setTrendingAnim("idle");
  }, [retailerConfig.retailer]);

  const showTrendingListRow =
    trendingCategories.length > 0 &&
    !isLoading &&
    filteredProducts.length > 0 &&
    productStatusFilter !== "saved";
  const currentTrendingCategory =
    trendingCategories[trendingIndex] || trendingCategories[0];
  const isTrendingActive = Boolean(
    currentTrendingCategory &&
      selectedCategories.length === 1 &&
      selectedCategories[0] === currentTrendingCategory.category &&
      productStatusFilter === "new"
  );

  const handleTrendingActivate = useCallback(() => {
    if (!currentTrendingCategory) return;
    if (isTrendingActive) {
      setSelectedCategories([]);
      setProductStatusFilter("all");
      return;
    }
    clearSearch();
    setSelectedSavedKeywords([]);
    setMinStorePrice(undefined);
    setMaxStorePrice(undefined);
    setMinMsrp(undefined);
    setMaxMsrp(undefined);
    setMinDiscount(undefined);
    setShowNewestOnly(false);
    setShowHotDeals(false);
    setShowPennyItems(false);
    setShowInStockOnly(true);
    setSelectedCategories([currentTrendingCategory.category]);
    setProductStatusFilter("new");
  }, [clearSearch, currentTrendingCategory, isTrendingActive]);

  const handleTrendingTouchStart = useCallback((event: TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    trendingTouchRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
    };
    setTrendingSwipeOffset(0);
  }, []);

  const handleTrendingTouchMove = useCallback((event: TouchEvent<HTMLDivElement>) => {
    if (!trendingTouchRef.current) return;
    const dx = event.touches[0].clientX - trendingTouchRef.current.startX;
    const dy = event.touches[0].clientY - trendingTouchRef.current.startY;
    if (Math.abs(dy) > Math.abs(dx)) return;
    event.preventDefault();
    setTrendingSwipeOffset(Math.max(-80, Math.min(80, dx * 0.5)));
  }, []);

  const handleTrendingTouchEnd = useCallback(() => {
    if (!trendingTouchRef.current) {
      setTrendingSwipeOffset(0);
      return;
    }
    const threshold = 30;
    const didSwipe =
      (trendingSwipeOffset < -threshold &&
        trendingIndex < trendingCategories.length - 1) ||
      (trendingSwipeOffset > threshold && trendingIndex > 0);
    if (didSwipe) {
      const next =
        trendingSwipeOffset < -threshold ? trendingIndex + 1 : trendingIndex - 1;
      goTrending(next);
    }
    trendingTouchRef.current = null;
    setTrendingSwipeOffset(0);
  }, [goTrending, trendingCategories.length, trendingIndex, trendingSwipeOffset]);

  const renderTrendingProductsRow = useCallback(() => {
    if (!showTrendingListRow || !currentTrendingCategory) return null;
    return (
      <TrendingProductsRow
        t={t}
        trendingCategories={trendingCategories}
        trendingIndex={trendingIndex}
        trendingSwipeOffset={trendingSwipeOffset}
        trendingAnim={trendingAnim}
        isTrendingActive={isTrendingActive}
        onActivate={handleTrendingActivate}
        onTouchStart={handleTrendingTouchStart}
        onTouchMove={handleTrendingTouchMove}
        onTouchEnd={handleTrendingTouchEnd}
        onPrev={() => goTrending(Math.max(0, trendingIndex - 1))}
        onNext={() =>
          goTrending(Math.min(trendingCategories.length - 1, trendingIndex + 1))
        }
      />
    );
  }, [
    currentTrendingCategory,
    goTrending,
    handleTrendingActivate,
    handleTrendingTouchEnd,
    handleTrendingTouchMove,
    handleTrendingTouchStart,
    isTrendingActive,
    showTrendingListRow,
    t,
    trendingAnim,
    trendingCategories,
    trendingIndex,
    trendingSwipeOffset,
  ]);

  // Update refs whenever they change
  useEffect(() => {
    filteredProductsRef.current = filteredProducts;
  }, [filteredProducts]);

  useEffect(() => {
    userZipCodeRef.current = user?.zipCode;
  }, [user?.zipCode]);

  // Handle scroll position restoration when returning to the page
  useEffect(() => {
    // Check if we're returning from a product details page
    const urlParams = new URLSearchParams(location.search);
    const returnToIndex = urlParams.get("returnToIndex");
    const currentRouteKey = `${location.pathname}${location.search}`;
    const pendingRetailerRoute = pendingRetailerScrollResetRouteRef.current;
    let restoreTimeout: number | undefined;

    // A retailer switch can render once on a bare route and again after
    // storeId normalization. Never let either render restore an old offset.
    if (pendingRetailerRoute) {
      resetScrollPosition();
      if (currentRouteKey === pendingRetailerRoute) {
        pendingRetailerScrollResetRouteRef.current = null;
      }
      return;
    }

    // Product pages grow and refresh in place. Replaying restoration whenever
    // the product count changes can jump an actively-scrolling user back to a
    // stale offset, so each route is restored only once after its list exists.
    if (
      restoredScrollRouteRef.current === currentRouteKey ||
      filteredProducts.length === 0 ||
      !virtualizerRef.current
    ) {
      return;
    }

    if (returnToIndex) {
      // Scroll to specific product index
      const productIndex = parseInt(returnToIndex, 10);
      if (
        !isNaN(productIndex) &&
        productIndex >= 0 &&
        productIndex < filteredProducts.length
      ) {
        // Use a small delay to ensure virtualizer is ready
        restoreTimeout = window.setTimeout(() => {
          restoredScrollRouteRef.current = currentRouteKey;
          scrollToProduct(
            showTrendingListRow && !isGallery ? productIndex + 1 : productIndex,
            virtualizerRef.current
          );
        }, 100);

        // Clean up the URL parameter
        const newParams = new URLSearchParams(location.search);
        newParams.delete("returnToIndex");
        const newSearch = newParams.toString();
        const newUrl = `${location.pathname}${
          newSearch ? `?${newSearch}` : ""
        }`;
        window.history.replaceState(null, "", newUrl);
      }
    } else if (!returnToIndex) {
      // Restore normal scroll position if no specific product index
      restoreTimeout = window.setTimeout(() => {
        restoredScrollRouteRef.current = currentRouteKey;
        restoreScrollPosition();
      }, 100);
    }

    return () => {
      if (restoreTimeout !== undefined) {
        window.clearTimeout(restoreTimeout);
      }
    };
  }, [
    filteredProducts.length,
    isGallery,
    location,
    resetScrollPosition,
    showTrendingListRow,
    scrollToProduct,
    restoreScrollPosition,
  ]);

  // Save scroll position before navigating away
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveScrollPosition();
    };

    // Save scroll position when the component unmounts or page changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        saveScrollPosition();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      // Save scroll position when component unmounts
      saveScrollPosition();
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [saveScrollPosition]);

  // Zoho widget positioning
  const showGoUpButton = filteredProducts.length > 0 && !isLoading && !isAtTop;
  const zohoWidgetProps = useZohoWidget({
    showGoUpButton,
    hasBottomNav: isUserSetUp(user),
  });

  // Check if onboarding modal should be shown when user or retailer changes
  // Skip for online-only retailers (woot, walmart_online) - they don't need stores
  const hasWorkingStoreProducts =
    !isOnlineRetailer &&
    !userLoading &&
    !isLoading &&
    ((allProductsData?.products?.length ?? 0) > 0 ||
      (productsData?.products?.length ?? 0) > 0);

  const needsStoreOnboarding =
    !userLoading &&
    !!user &&
    !isLoading &&
    retailerConfig.isFeatureEnabled("stores") &&
    userStoreIds.length === 0 &&
    !hasWorkingStoreProducts &&
    selectedStoreId !== "online" &&
    retailerConfig.retailer !== "woot" &&
    retailerConfig.retailer !== "walmart_online" &&
    retailerConfig.retailer !== "target_online" &&
    retailerConfig.retailer !== "amazon" &&
    retailerConfig.retailer !== "kohls" &&
    !isExtraOnlineRetailer(retailerConfig.retailer) &&
    !isWalmartOnlineMode;

  useEffect(() => {
    if (!needsStoreOnboarding) {
      setShowOnboardingModal(false);
      if (userStoreIds.length > 0) setDismissedStoreOnboardingFor(null);
      return;
    }
    setShowOnboardingModal(dismissedStoreOnboardingFor !== retailerConfig.retailer);
  }, [
    dismissedStoreOnboardingFor,
    needsStoreOnboarding,
    retailerConfig.retailer,
    userStoreIds.length,
  ]);

  // Derive categories from products filtered by everything EXCEPT the category filter,
  // so counts reflect search, price, stock, newest, status dropdown, store, etc.
  const categories = useMemo((): (CategoryV2 & { count: number })[] => {
    if (browseEnabled && browseMetadata) {
      return PRODUCT_CATEGORIES.map(({ id }) => ({
        rootKey: id, subcategories: [], count: browseMetadata.categoryCounts.find(category => category.id === id)?.count ?? 0,
      }));
    }
    const perfStart = performance.now();
    if (!productsData?.products || !Array.isArray(productsData.products)) {
      return [];
    }
    const nextCategories = getCachedProductsPageComputation(
      productsData.products,
      makeProductsPageCacheKey(
        "categories",
        showNewestOnly,
        debouncedSearchTerm,
        minStorePrice,
        maxStorePrice,
        minMsrp,
        maxMsrp,
        minDiscount,
        showInStockOnly,
        showVariantsOnly,
        hideCashback,
        retailerConfig.retailer,
        isOnlineRetailer,
        productStatusFilter,
        selectedStoreId
      ),
      () => {
        const baseFiltered = applyBaseFilters({
          products: productsData.products,
          selectedCategories: [],
          showNewestOnly,
          searchTerm: debouncedSearchTerm,
          minStorePrice,
          maxStorePrice,
          minMsrp,
          maxMsrp,
          minDiscount,
          showInStockOnly,
          showVariantsOnly,
          hideCashback,
          retailer: retailerConfig.retailer,
          isOnlineRetailer,
        });
        const fullyFiltered = productStatusFilter === "all" && selectedStoreId === "all"
          ? baseFiltered
          : applyStatusAndStoreFilters({
              products: baseFiltered,
              productStatusFilter,
              selectedStoreId,
              retailer: retailerConfig.retailer,
              isOnlineRetailer,
            });
        return deriveCategories(fullyFiltered);
      }
    );
    logProductsPagePerf("deriveCategories", perfStart, {
      retailer: retailerConfig.retailer,
      candidateProducts: productsData.products.length,
      categoryCount: nextCategories.length,
      selectedStoreId,
      productStatusFilter,
    });
    return nextCategories;
  }, [productsData?.products, showNewestOnly, debouncedSearchTerm, minStorePrice, maxStorePrice, minMsrp, maxMsrp, minDiscount, showInStockOnly, showVariantsOnly, hideCashback, retailerConfig.retailer, isOnlineRetailer, productStatusFilter, selectedStoreId, browseEnabled, browseMetadata]);

  const categoriesLoading = isLoading;


  // Refresh both tab sources when their counts are available.
  const handleRefresh = useCallback(() => {
    void hiddenProductsQuery.refetch();
    if (browseEnabled || (!isFreeUser && !needsClientAllProducts)) void browseQuery.refetch();
    if (needsClientAllProducts) void legacyProductsQuery.refetch();
    if ((dealViewMode === "top" || topProductsQuery.data !== undefined) &&
        (!needsClientAllProducts || apiStoreId !== topApiStoreId)) {
      void topProductsQuery.refetch();
    }
    if (productStatusFilter === "saved") void refetchFavorites();
  }, [hiddenProductsQuery.refetch, browseEnabled, isFreeUser, needsClientAllProducts,
    browseQuery.refetch, legacyProductsQuery.refetch, topProductsQuery.refetch,
    topProductsQuery.data, dealViewMode, apiStoreId, topApiStoreId, productStatusFilter, refetchFavorites]);

  // Invalidate React Query cache when showHotDeals or showPennyItems changes (but not on initial render)
  // Skip for online retailers - they don't support these filters
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    // Skip for online retailers - they don't use showHotDeals/showPennyItems
    if (isOnlineRetailer) return;

    queryClient.invalidateQueries({
      queryKey: getProductsV2QueryKey(
        retailerConfig.retailer,
        showHotDeals,
        showPennyItems,
        topApiStoreId
      ),
    });
  }, [
    showHotDeals,
    showPennyItems,
    queryClient,
    retailerConfig.retailer,
    topApiStoreId,
    isOnlineRetailer,
  ]);

  // Tell the virtualizer not to adjust scroll while a row expand/collapse animation is running.
  const handleToggleExpanded = useCallback(() => {
    suppressScrollAdjustRef.current = true;
    if (suppressScrollAdjustTimeoutRef.current !== null) {
      window.clearTimeout(suppressScrollAdjustTimeoutRef.current);
    }
    suppressScrollAdjustTimeoutRef.current = window.setTimeout(() => {
      suppressScrollAdjustRef.current = false;
      suppressScrollAdjustTimeoutRef.current = null;
    }, EXPAND_SCROLL_LOCK_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (suppressScrollAdjustTimeoutRef.current !== null) {
        window.clearTimeout(suppressScrollAdjustTimeoutRef.current);
      }
    };
  }, []);

  // Perform the actual search
  const performSearch = useCallback(
    async (
      code: string,
      type: "UPC" | "SKU",
      productId: string,
      zipCode: string,
      purpose: "nearby" | "barcode" = "nearby"
    ) => {
      setSearchProductId(productId);
      setIsSearching(true);
      setSearchError(null);
      setSearchResults([]);
      setHomeDepotSearchResults([]);
      setSearchAmazonData(null);
      if (purpose === "nearby") {
        setShowNearbyResultsModal(true);
      }

      try {
        if (retailerConfig.retailer === "home_depot") {
          const response = await searchHomeDepotProduct(code.trim(), zipCode);
          setHomeDepotSearchResults(response.results);
          if (response.amazonPrice != null && response.amazonPrice > 0) {
            setSearchAmazonData({
              price: response.amazonPrice, title: response.amazonTitle,
              url: response.amazonURL, isPrime: response.amazonIsPrime,
            });
          }
          if (response.results.length === 0) {
            setSearchError(t("products.noResultsForProduct"));
          }
        } else if (retailerConfig.retailer === "lowes") {
          const product = productsData?.products?.find(
            (p) => p.upc === productId || p.sku === productId || p.upc === code || p.sku === code
          );
          const response = await searchLowesNearby({
            omniItemId: product?.upc || code,
            itemNumber: product?.sku || code,
            zipCode,
            name: product?.name,
            image: product?.image,
            url: product?.url,
            storePrice: product?.storePrice?.[product.storePrice.length - 1],
            msrp: product?.msrp,
          });
          setSearchResults(response.results);
          if (response.amazonPrice != null && response.amazonPrice > 0) {
            setSearchAmazonData({
              price: response.amazonPrice, title: response.amazonTitle,
              url: response.amazonURL, isPrime: response.amazonIsPrime,
            });
          }
          if (response.results.length === 0) {
            setSearchError(t("products.noResultsForProduct"));
          }
        } else {
          // For Walmart, Dollar General, and other retailers, use the existing search
          const response = await searchProduct(
            code.trim(),
            type,
            zipCode,
            retailerConfig.retailer as
              | "walmart"
              | "home_depot"
              | "target"
              | "dollar_general"
              | "costco"
              | "bestbuy"
              | "tractor_supply"
          );
          setSearchResults(response.results);
          if (response.amazonPrice != null && response.amazonPrice > 0) {
            setSearchAmazonData({
              price: response.amazonPrice, title: response.amazonTitle,
              url: response.amazonURL, isPrime: response.amazonIsPrime,
            });
          }
          setIsSearching(false);

          if (response.results.length === 0) {
            setSearchError(t("products.noResultsForProduct"));
          }
        }
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : t("products.failedToSearchProduct");
        setSearchError(message);
      } finally {
        setIsSearching(false);
      }
    },
    [retailerConfig.retailer, productsData?.products, t]
  );

  // Product search functionality
  const handleProductSearch = useCallback(
    async (
      code: string,
      type: "UPC" | "SKU" = "UPC",
      productId?: string,
      purpose: "nearby" | "barcode" = "nearby"
    ) => {
      if (!code.trim()) {
        setSearchError(t("products.enterCodeToSearch"));
        return;
      }

      const currentProductId = productId || code; // Use productId or fallback to code
      // Check if user has a saved zip code
      if (!userZipCodeRef.current) {
        // Store the search parameters and show zip code modal
        setPendingSearch({ code, type, productId: currentProductId, purpose });
        setShowZipCodeModal(true);
        return;
      }

      // Proceed with search using user's zip code
      await performSearch(code, type, currentProductId, userZipCodeRef.current, purpose);
    },
    [performSearch, t]
  );

  // Handle search reset
  const handleSearchReset = useCallback(
    (productId: string) => {
      if (searchProductId === productId) {
        setSearchResults([]);
        setHomeDepotSearchResults([]);
        setSearchAmazonData(null);
        setSearchError(null);
        setSearchProductId(null);
        setShowNearbyResultsModal(false);
      }
    },
    [searchProductId]
  );

  // Handle zip code modal success
  const handleZipCodeSuccess = useCallback(
    (zipCode: string) => {
      setShowZipCodeModal(false);

      // If there's a pending search, perform it now
      if (pendingSearch) {
        performSearch(
          pendingSearch.code,
          pendingSearch.type,
          pendingSearch.productId,
          zipCode,
          pendingSearch.purpose
        );
        setPendingSearch(null);
      }
    },
    [pendingSearch, performSearch]
  );

  // Crazy deals and Penny items read different store partitions, so only one
  // can be on at a time.
  const handleCrazyDealsToggle = useCallback((active: boolean) => {
    // Let the tab underline move first; the feed swap renders in a transition.
    startTransition(() => {
      if (active) setShowPennyItems(false);
      setShowHotDeals(active);
    });
  }, [startTransition]);

  // Handle penny items toggle with disclaimer
  const handlePennyItemsToggle = useCallback(() => {
    if (retailerConfig.retailer === "home_depot") {
      if (canUsePennyBeta) {
        setShowPennyModeChooser(true);
      } else {
        navigate("/home_depot/penny-items-pro");
      }
      return;
    }
    // If turning on penny items and user hasn't seen disclaimer
    if (!showPennyItems) {
      const hasSeenDisclaimer = localStorage.getItem(
        PENNY_ITEMS_DISCLAIMER_KEY
      );
      if (!hasSeenDisclaimer) {
        setShowPennyItemsDisclaimer(true);
        return;
      }
      // DG commonly zeroes reported inventory with its $0.00 penny marker.
      // Keep the historical penny list visible unless stock-only is chosen.
      if (retailerConfig.retailer === "dollar_general") {
        setShowInStockOnly(false);
        setSortBy("firstAppeared");
        setSortDirection("desc");
      }
      setShowHotDeals(false);
      // The Top feed drops $0.00 rows, so the penny list only renders on All.
      handleDealViewModeChange("all");
    }

    // Toggle penny items directly
    setShowPennyItems(!showPennyItems);
  }, [showPennyItems, PENNY_ITEMS_DISCLAIMER_KEY, retailerConfig.retailer, handleDealViewModeChange, canUsePennyBeta, navigate]);

  // Handle disclaimer acceptance
  const handleDisclaimerAccept = useCallback(() => {
    // Mark as seen in localStorage
    localStorage.setItem(PENNY_ITEMS_DISCLAIMER_KEY, "true");

    // Close modal and enable penny items
    setShowPennyItemsDisclaimer(false);
    if (retailerConfig.retailer === "dollar_general") {
      setShowInStockOnly(false);
      setSortBy("firstAppeared");
      setSortDirection("desc");
    }
    setShowHotDeals(false);
    handleDealViewModeChange("all");
    setShowPennyItems(true);
  }, [PENNY_ITEMS_DISCLAIMER_KEY, retailerConfig.retailer, handleDealViewModeChange]);

  // Penny deals pill: Home Depot opens Penny Items Pro, Dollar General toggles its penny list.
  const handlePennyDealsPress = useCallback(() => {
    if (retailerConfig.retailer === "home_depot") {
      if (canUsePennyBeta) {
        setShowPennyModeChooser(true);
      } else {
        navigate("/home_depot/penny-items-pro");
      }
      return;
    }
    handlePennyItemsToggle();
  }, [canUsePennyBeta, handlePennyItemsToggle, navigate, retailerConfig.retailer]);

  // Handle disclaimer close
  const handleDisclaimerClose = useCallback(() => {
    setShowPennyItemsDisclaimer(false);
  }, []);

  // Handle onboarding completion
  const handleOnboardingComplete = useCallback(() => {
    // Refresh the page to see the new state
    window.location.reload();
  }, []);

  const handleOnboardingClose = useCallback(() => {
    setDismissedStoreOnboardingFor(retailerConfig.retailer);
    setShowOnboardingModal(false);
  }, [retailerConfig.retailer]);

  const handleSavedKeywordsChange = useCallback(
    async (keywords: string[]) => {
      await updateUser({ searchTerms: sanitizeSavedKeywords(keywords) });
    },
    [updateUser]
  );

  const searchAccessory = useMemo(
    () => (
      <SavedKeywordsButton
        searchTerm={searchTerm}
        savedKeywords={savedKeywords}
        keywordMatchCounts={keywordMatchCounts}
        onSavedKeywordsChange={handleSavedKeywordsChange}
        selectedSavedKeywords={selectedSavedKeywords}
        onSelectedSavedKeywordsChange={setSelectedSavedKeywords}
      />
    ),
    [searchTerm, savedKeywords, keywordMatchCounts, handleSavedKeywordsChange, selectedSavedKeywords]
  );

  // Pre-compute products by UPC map for efficient lookup in ProductItem
  const productsByUPC = useMemo(() => {
    if (isTopMode) return new Map<string, ProductListItem[]>();
    return buildProductsByUpc(commonFilteredProducts);
  }, [commonFilteredProducts, isTopMode]);

  // Handlers for the curated Top deals view (switch back to "all" mode, optionally
  // setting the selected category as the only active filter).
  const handleSeeAllFromTop = useCallback(() => {
    handleDealViewModeChange("all");
  }, [handleDealViewModeChange]);
  const handleTopSelectCategory = useCallback(
    (category: string) => {
      setSelectedCategories([category]);
      setProductStatusFilter("all");
      handleDealViewModeChange("all");
    },
    [handleDealViewModeChange]
  );
  const handleSeeAllNewFromTop = useCallback(() => {
    setSelectedCategories([]);
    setProductStatusFilter("new");
    handleDealViewModeChange("all");
  }, [handleDealViewModeChange]);

  // Route the Top deals "nearby" (Stock) button through the same handler used by
  // ProductItem so it respects the saved zip code and shows the zip modal when missing.
  const handleCheckStockSearch = useCallback(
    async (params: {
      searchCode: string;
      searchType: "UPC" | "SKU";
      fallbackCode?: string;
      fallbackType?: "SKU";
    }) => {
      await handleProductSearch(
        params.searchCode,
        params.searchType,
        params.searchCode,
        "nearby"
      );
    },
    [handleProductSearch]
  );

  // Handle removing a saved product
  const handleRemoveSaved = useCallback((upc: string) => {
    removeFavoriteMutation.mutate(upc, {
      onSuccess: () => refetchFavorites(),
    });
  }, [removeFavoriteMutation, refetchFavorites]);

  // Prepare data for the list - memoized to prevent unnecessary re-renders
  const listData = useMemo(() => {
    return {
      products: filteredProducts,
      productsByUPC, // Pre-computed map for efficient lookup
      storeCounts: browseStoreCounts,
      showHotDeals,
      showPennyItems,
      categories,
      homeDepotCategories,
      onToggleExpanded: handleToggleExpanded,
      onProductSearch: handleProductSearch,
      onSearchReset: handleSearchReset,
      searchResults,
      homeDepotSearchResults,
      searchAmazonData,
      searchError,
      searchProductId,
      isSearching,
      retailer: retailerConfig.retailer,
      selectedStoreId, // Pass selected store for display purposes
      storeFilterOptions, // Pass store options for multi-store view
      loadedStores: loadedStores || undefined, // Pass loaded stores for coordinate lookup
      isSavedMode: productStatusFilter === "saved",
      onRemoveSaved: handleRemoveSaved,
      onHideProduct: user?.userId ? handleHideProduct : undefined,
      disableFavorites: isFreeUser,
      sortBy,
    };
  }, [
    filteredProducts,
    productsByUPC,
    browseStoreCounts,
    showHotDeals,
    showPennyItems,
    categories,
    homeDepotCategories,
    handleToggleExpanded,
    handleProductSearch,
    handleSearchReset,
    searchResults,
    homeDepotSearchResults,
    searchAmazonData,
    searchError,
    searchProductId,
    isSearching,
    retailerConfig.retailer, // Only depend on retailer string, not entire config
    selectedStoreId,
    storeFilterOptions,
    loadedStores,
    productStatusFilter,
    handleRemoveSaved,
    handleHideProduct,
    isFreeUser,
    user?.userId,
    sortBy,
  ]);

  // Smaller virtual overscan on iOS: fewer heavy ProductItem rows mounted while flick-scrolling
  const isIOSMobile = useMemo(() => isIosLikeDevice(), []);
  const listVirtualOverscan = isIOSMobile ? 1 : 8;
  const galleryVirtualOverscan = isIOSMobile ? 1 : 6;

  // Setup virtualizer - only active in list mode
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<number | null>(null);
  const isAtTopForFabRef = useRef(true);

  const virtualizer = useVirtualizer({
    count: isGallery ? 0 : filteredProducts.length + (showTrendingListRow ? 1 : 0),
    getScrollElement: () => (isGallery ? null : parentRef.current),
    estimateSize: () => 130,
    overscan: listVirtualOverscan,
    useScrollendEvent: true,
    enabled: !isGallery,
  });

  useEffect(() => {
    virtualizer.shouldAdjustScrollPositionOnItemSizeChange = () =>
      !suppressScrollAdjustRef.current && !isScrollingRef.current;
  }, [virtualizer]);

  const galleryColumns = useMemo(() => {
    // Two columns on phones, like the app; widen as the viewport grows.
    if (galleryViewportWidth < 640) return 2;
    if (galleryViewportWidth < 900) return 3;
    if (galleryViewportWidth < 1180) return 4;
    if (galleryViewportWidth < 1480) return 5;
    return 6;
  }, [galleryViewportWidth]);

  const galleryRowCount = useMemo(
    () =>
      Math.ceil(filteredProducts.length / Math.max(1, galleryColumns)) +
      (showTrendingListRow ? 1 : 0),
    [filteredProducts.length, galleryColumns, showTrendingListRow]
  );

  // Gallery virtualizer - only active in gallery mode
  const galleryVirtualizer = useVirtualizer({
    count: isGallery ? galleryRowCount : 0,
    getScrollElement: () => (isGallery ? parentRef.current : null),
    estimateSize: () => 290,
    overscan: galleryVirtualOverscan,
    useScrollendEvent: true,
    enabled: isGallery,
  });

  useEffect(() => {
    galleryVirtualizer.shouldAdjustScrollPositionOnItemSizeChange = () =>
      !isScrollingRef.current;
  }, [galleryVirtualizer]);

  // Virtualizers retain their measured offset across retailer renders. Reset
  // them before paint, then once more after the next layout measurement so the
  // previous retailer's offset cannot be applied to the new list.
  useLayoutEffect(() => {
    if (renderedScrollRetailerRef.current === retailerConfig.retailer) return;
    renderedScrollRetailerRef.current = retailerConfig.retailer;

    const resetRenderedScrollPosition = () => {
      const scrollElements = new Set<HTMLElement>();
      if (parentRef.current) scrollElements.add(parentRef.current);
      listContainerRef.current
        ?.querySelectorAll<HTMLElement>("[data-products-scroll-container]")
        .forEach((element) => scrollElements.add(element));

      scrollElements.forEach((element) => {
        element.scrollTop = 0;
        element.scrollLeft = 0;
      });

      virtualizer.scrollToOffset(0, { align: "start", behavior: "auto" });
      galleryVirtualizer.scrollToOffset(0, { align: "start", behavior: "auto" });
    };

    resetRenderedScrollPosition();
    const frame = window.requestAnimationFrame(resetRenderedScrollPosition);
    return () => window.cancelAnimationFrame(frame);
  }, [galleryVirtualizer, retailerConfig.retailer, virtualizer]);

  // Store the currently active virtualizer reference for shared scroll actions
  useEffect(() => {
    virtualizerRef.current = isGallery ? galleryVirtualizer : virtualizer;
  }, [virtualizer, galleryVirtualizer, isGallery]);

  // Track gallery viewport width to compute responsive column count
  useEffect(() => {
    if (viewMode !== "gallery") return;
    const element = parentRef.current;
    if (!element) return;

    const updateWidth = () => {
      setGalleryViewportWidth(element.clientWidth);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, [viewMode, filteredProducts.length]);

  /** FAB: only setState when crossing 200px; isScrolling for virtualizer size adjustments. */
  useEffect(() => {
    if (isLoading || filteredProducts.length === 0) return;
    const el = parentRef.current;
    if (!el) return;

    const onScroll = () => {
      const st = el.scrollTop;
      const atTop = st < 200;
      if (atTop !== isAtTopForFabRef.current) {
        isAtTopForFabRef.current = atTop;
        setIsAtTop(atTop);
      }
      isScrollingRef.current = true;
      if (scrollTimeoutRef.current) window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = window.setTimeout(() => {
        isScrollingRef.current = false;
      }, 150);
    };

    isAtTopForFabRef.current = el.scrollTop < 200;
    setIsAtTop(isAtTopForFabRef.current);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (scrollTimeoutRef.current) window.clearTimeout(scrollTimeoutRef.current);
    };
  }, [isLoading, filteredProducts.length, isGallery, viewMode, galleryColumns]);

  // Handle store change (physical stores only — never "online")
  const handleStoreChange = useCallback(
    (storeId: string) => {
      if (isFreeUser && storeId !== "online") {
        return;
      }
      setSelectedStoreId(storeId);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("storeId", storeId);
        return newParams;
      });
    },
    [isFreeUser, setSearchParams]
  );

  // A hidden extra store is never the selection (hidden here, from a stale
  // link, or on another device): fall back to all stores.
  useEffect(() => {
    if (selectedStoreId === "all" || selectedStoreId === "online") return;
    if (activeHiddenExtraIds.has(formatExtraStoreId(selectedStoreId))) handleStoreChange("all");
  }, [activeHiddenExtraIds, handleStoreChange, selectedStoreId]);

  const [extraStoreUndo, setExtraStoreUndo] = useState<{ retailer: string; storeId: string; reselect: boolean; at: number } | null>(null);
  const dismissExtraStoreUndo = useCallback(() => setExtraStoreUndo(null), []);
  const handleHideExtraStore = useCallback(
    (storeId: string) => {
      const retailer = retailerConfig.retailer;
      if (!hideExtraStore(retailer, storeId)) return;
      const reselect = formatExtraStoreId(selectedStoreId) === formatExtraStoreId(storeId);
      if (reselect) handleStoreChange("all");
      setExtraStoreUndo({ retailer, storeId, reselect, at: Date.now() });
    },
    [handleStoreChange, hideExtraStore, retailerConfig.retailer, selectedStoreId]
  );
  const handleShowExtraStore = useCallback(
    (storeId: string) => {
      showExtraStore(retailerConfig.retailer, storeId);
      setExtraStoreUndo((current) => (current && formatExtraStoreId(current.storeId) === formatExtraStoreId(storeId) ? null : current));
    },
    [retailerConfig.retailer, showExtraStore]
  );
  const handleUndoHideExtraStore = useCallback(() => {
    if (!extraStoreUndo) return;
    showExtraStore(extraStoreUndo.retailer, extraStoreUndo.storeId);
    if (extraStoreUndo.reselect && extraStoreUndo.retailer === retailerConfig.retailer) handleStoreChange(extraStoreUndo.storeId);
    setExtraStoreUndo(null);
  }, [extraStoreUndo, handleStoreChange, retailerConfig.retailer, showExtraStore]);

  // Handle swipe navigation between store filters
  const handleSwipeLeft = useCallback(() => {
    if (storeFilterOptions.length <= 1) return;

    const currentIndex = storeFilterOptions.findIndex(
      (option) => option.id === selectedStoreId
    );
    if (currentIndex !== -1) {
      const nextIndex = (currentIndex + 1) % storeFilterOptions.length;

      // Start animation immediately
      setSwipeDirection("left");
      setIsAnimatingSwipe(true);

      // Change store immediately
      handleStoreChange(storeFilterOptions[nextIndex].id);

      // Reset animation after short delay
      setTimeout(() => {
        setIsAnimatingSwipe(false);
        setSwipeDirection(null);
      }, 200);
    }
  }, [storeFilterOptions, selectedStoreId, handleStoreChange]);

  const handleSwipeRight = useCallback(() => {
    if (storeFilterOptions.length <= 1) return;

    const currentIndex = storeFilterOptions.findIndex(
      (option) => option.id === selectedStoreId
    );
    if (currentIndex !== -1) {
      const prevIndex =
        currentIndex === 0 ? storeFilterOptions.length - 1 : currentIndex - 1;

      // Start animation immediately
      setSwipeDirection("right");
      setIsAnimatingSwipe(true);

      // Change store immediately
      handleStoreChange(storeFilterOptions[prevIndex].id);

      // Reset animation after short delay
      setTimeout(() => {
        setIsAnimatingSwipe(false);
        setSwipeDirection(null);
      }, 200);
    }
  }, [storeFilterOptions, selectedStoreId, handleStoreChange]);

  // Handle live swipe movement
  const handleSwipeMove = useCallback((deltaX: number) => {
    // Cap the offset to prevent over-swiping
    const maxOffset = 100; // Maximum pixels to move
    const cappedOffset = Math.max(
      -maxOffset,
      Math.min(maxOffset, deltaX * 0.5)
    );
    setSwipeOffset(cappedOffset);
  }, []);

  // Handle swipe start
  const handleSwipeStart = useCallback(() => {
    setIsSwiping(true);
    setSwipeOffset(0);
  }, []);

  // Handle swipe end
  const handleSwipeEnd = useCallback(() => {
    setIsSwiping(false);
    // Animate back to center if not committing a swipe
    setSwipeOffset(0);
  }, []);

  // Setup swipe detection (only enable if user has multiple store options).
  // Top deals view aggregates across all stores, so the store-switching swipe gesture is disabled there.
  const swipeRef = useSwipeDetection<HTMLDivElement>({
    onSwipeLeft: handleSwipeLeft,
    onSwipeRight: handleSwipeRight,
    onSwipeStart: handleSwipeStart,
    onSwipeEnd: handleSwipeEnd,
    onSwipeMove: handleSwipeMove,
    enableSwipe:
      !isFreeUser && storeFilterOptions.length > 1 && dealViewMode !== "top",
    minSwipeDistance: 80,
    maxSwipeTime: 400,
  });

  // Show swipe hint for users with multiple store options (only once per session)
  useEffect(() => {
    if (
      !isFreeUser &&
      storeFilterOptions.length > 1 &&
      filteredProducts.length > 0 &&
      !showSwipeHint
    ) {
      // Check if hint was already shown in this session
      const hintShown = sessionStorage.getItem("swipeHintShown");
      if (!hintShown) {
        const timer = setTimeout(() => {
          setShowSwipeHint(true);
          sessionStorage.setItem("swipeHintShown", "true");
          // Hide hint after 3 seconds
          setTimeout(() => setShowSwipeHint(false), 3000);
        }, 2000); // Show hint 2 seconds after initial load

        return () => clearTimeout(timer);
      }
    }
  }, [isFreeUser, storeFilterOptions.length, filteredProducts.length, showSwipeHint]);

  // Handle categories change
  const handleCategoriesChange = (categoryIds: string[]) => {
    resetScrollPosition();
    isAtTopForFabRef.current = true;
    setIsAtTop(true);
    // The filter sheet emits canonical values: [] = all, ["__none__"] = none.
    setSelectedCategories(categoryIds);
  };

  // Handle retry
  const handleRetry = () => {
    handleRefresh();
  };

  const handleResetFilters = () => {
    const resetStoreId =
      isFreeUser ? "online" : selectedStoreId === "online" ? "online" : "all";
    handleStoreChange(resetStoreId);
    setSelectedCategories(normalizeCategorySelection(propCategory ? [propCategory] : []));
    clearSearch();
    setSelectedSavedKeywords([]);
    setSortBy(getDefaultSortBy(retailerConfig.retailer, resetStoreId));
    setSortDirection("desc");
    setMinStorePrice(undefined);
    setMaxStorePrice(undefined);
    setMinMsrp(undefined);
    setMaxMsrp(undefined);
    setMinDiscount(undefined);
    setShowNewestOnly(false);
    setShowVariantsOnly(false);
    setHideCashback(DEFAULT_HIDE_CASHBACK);
    setShowHotDeals(false);
    setShowPennyItems(false);
    setShowInStockOnly(true);
    setProductStatusFilter("all");
  };

  const showReferralInviteModal =
    MEMBER_REFERRALS_ENABLED &&
    !userLoading &&
    !!user &&
    // Established paid members only: at least four days old and actively entitled.
    !isFreeUser &&
    (!user.created || Date.now() - Number(user.created) >= 4 * 24 * 60 * 60 * 1000) &&
    !user.referralInviteDismissed &&
    dismissedReferralInviteForUserId !== user.userId &&
    !showFreeWelcomeCard &&
    !showZipCodeModal &&
    !showNearbyResultsModal &&
    !showPennyItemsDisclaimer &&
    !showOnboardingModal &&
    !showHiddenProductsModal &&
    !queryResumeIntent;

  // Show loading while user context is loading
  if (userLoading) {
    return <LoadingState message={t("products.loadingUserData")} />;
  }


  // Check if user has access to the current retailer
  if (user && !isFreeUser) {
    const userRetailers = getUserRetailers(user);
    const hasAccess = getRetailerAccess({
      retailer,
      userRetailers,
      retailerAccessOverrides: user.retailerAccessOverrides,
      isOnline: isOnlineRetailer,
    });

    if (!hasAccess) {
      const retailerLabel = getRetailerLabel(retailer);
      return (
        <EmptyState
          title={t("products.accessRestricted")}
          message={t("products.accessRestrictedMessage", { retailer: retailerLabel })}
          actionButton={{
            text: t("products.manageBilling"),
            to: "/settings?tab=billing",
            icon: <CreditCard size={20} />,
          }}
        />
      );
    }
  }

  // Show message if user has no stores assigned (but don't show if onboarding modal is showing)
  // Skip for online-only retailers (woot, walmart_online) - they don't need stores
  if (
    user &&
    !isFreeUser &&
    userStoreIds.length === 0 &&
    !propStoreId &&
    !showOnboardingModal &&
    !isOnlineRetailer
  ) {
    return (
      <EmptyState
        title={t("products.noStoresAssigned")}
        message={t("products.noStoresAssignedMessage")}
        actionButtons={[
          { text: t("products.assignStores"), to: `/${retailer}/bot-filters`, icon: <Settings size={20} />, variant: "primary" },
          { text: t("card.browseOnlineDeals"), to: "/walmart/products?storeId=online", icon: <Flame size={18} />, variant: "secondary" },
        ]}
      />
    );
  }

  // Show error state only if there's an error and no products
  if (error && filteredProducts.length === 0 && !isLoading) {
    // Some retailers (Best Buy, Costco) reject the products request outright
    // when the account has no stores for them. That error must not hide the
    // store picker — otherwise the user is stuck on "Try Again" with no way to
    // assign stores. Render the no-stores state and keep the onboarding modal
    // mounted so it can open on top of it.
    if (user && !isFreeUser && needsStoreOnboarding && !propStoreId && !isOnlineRetailer) {
      return (
        <>
          <EmptyState
            title={t("products.noStoresAssigned")}
            message={t("products.noStoresAssignedMessage")}
            actionButtons={[
              { text: t("products.assignStores"), to: `/${retailer}/bot-filters`, icon: <Settings size={20} />, variant: "primary" },
              { text: t("card.browseOnlineDeals"), to: "/walmart/products?storeId=online", icon: <Flame size={18} />, variant: "secondary" },
            ]}
          />
          <BotFiltersOnboardingModal
            isOpen={showOnboardingModal}
            onComplete={handleOnboardingComplete}
            onClose={handleOnboardingClose}
          />
        </>
      );
    }
    return (
      <ErrorState
        title={t("products.failedToLoad")}
        message={error instanceof Error ? error.message : String(error)}
        onRetry={handleRetry}
      />
    );
  }


  return (
    <CheckStockContext.Provider value={handleCheckStockSearch}>
    <div
      ref={swipeRef}
      className={
        isIOSMobile
          ? "flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-dark-950 overscroll-none touch-manipulation min-h-0"
          : "flex-1 flex flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 dark:from-dark-950 dark:to-dark-900 overscroll-none touch-manipulation min-h-0"
      }
    >
      {showFreeWelcomeCard && (
        <FreePreviewWelcomeCard
          title={freeIntentCopy.welcomeTitle}
          subtitle={freeIntentCopy.welcomeSubtitle}
          t={t}
          onDismiss={() => {
            setDismissedFreeWelcome(true);
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete("welcome");
              next.delete("intent");
              return next;
            });
          }}
          onUpgrade={() => navigate("/settings?tab=billing&upgradeSource=free_preview_welcome")}
        />
      )}
      {!showFreeWelcomeCard && <EmailOptInCard />}
      {browseMetadata?.fallbackStoreIds?.length ? (
        <div className="mx-4 sm:mx-0 mb-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 ring-1 ring-amber-200 dark:ring-amber-500/20 px-4 py-3 text-sm">
          <p className="font-semibold text-amber-900 dark:text-amber-200">Today's update is still on its way</p>
          <p className="text-amber-800/80 dark:text-amber-200/70 mt-0.5">
            Showing the latest deals from the past {browseMetadata.fallbackDays} days for{" "}
            {browseMetadata.fallbackStoreIds.map((id) => `${retailerConfig.config.displayName} #${id}`).join(", ")} while we refresh. New deals appear automatically.
          </p>
        </div>
      ) : null}
      {!error && !showFreeNearbyPreview && (showDealAccessSwitcher || !isFreeUser) && (
        <div className="flex-shrink-0">
          <DealsAccessSwitcher
            viewMode={pendingDealViewMode}
            topDealCount={heldCounts.top}
            allDealCount={heldCounts.all}
            onViewModeChange={handleDealViewModeChange}
            showCrazyDeals={supportsCrazyDeals(retailerConfig.retailer)}
            crazyDealsActive={showHotDeals}
            onCrazyDealsToggle={handleCrazyDealsToggle}
            showPennyDeals={supportsPennyItems(retailerConfig.retailer)}
            pennyDealsActive={retailerConfig.retailer === "home_depot" ? undefined : showPennyItems}
            onPennyDealsPress={handlePennyDealsPress}
            crazyDealCount={heldCounts.crazy}
            pennyDealCount={retailerConfig.retailer === "home_depot" ? undefined : heldCounts.penny}
            onCouponsPress={retailerConfig.retailer === "dollar_general" ? () => navigate("/dollar_general/coupons") : undefined}
          />
        </div>
      )}
      {!showFreeNearbyPreview && <div className="relative z-10 flex-shrink-0">
        <ProductsHeader
          hasRefineFilters={hasActiveFilters({ selectedCategories, showNewestOnly, showVariantsOnly, hideCashback, showHotDeals, showPennyItems, minStorePrice, maxStorePrice, minMsrp, maxMsrp, minDiscount })}
          searchAccessory={searchAccessory}
          error={
            error instanceof Error
              ? error.message
              : error
              ? String(error)
              : null
          }
          productsCount={browseEnabled ? browseTotal ?? filteredProducts.length : filteredProducts.length}
          onRetry={handleRetry}
          selectedStoreId={selectedStoreId}
          onStoreChange={handleStoreChange}
          storeFilterOptions={storeFilterOptions}
          hiddenExtraStoreOptions={hiddenExtraStoreOptions}
          onHideExtraStore={user?.userId ? handleHideExtraStore : undefined}
          onShowExtraStore={user?.userId ? handleShowExtraStore : undefined}
          selectedCategories={selectedCategories}
          onCategoriesChange={handleCategoriesChange}
          categories={categories}
          categoriesLoading={categoriesLoading}
          showNewestOnly={showNewestOnly}
          onShowNewestOnlyChange={setShowNewestOnly}
          showVariantsOnly={showVariantsOnly}
          onShowVariantsOnlyChange={setShowVariantsOnly}
          hideCashback={hideCashback}
          onHideCashbackChange={setHideCashback}
          showHotDeals={showHotDeals}
          setShowHotDeals={handleCrazyDealsToggle}
          showPennyItems={showPennyItems}
          setShowPennyItems={handlePennyItemsToggle}
          hasOnlineOption={hasOnlineOption}
          searchTerm={searchTerm}
          onSearchChange={dealViewMode === "top" ? undefined : handleSearchChange}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={setSortBy}
          onSortDirectionChange={setSortDirection}
          minStorePrice={minStorePrice}
          maxStorePrice={maxStorePrice}
          minMsrp={minMsrp}
          maxMsrp={maxMsrp}
          minDiscount={minDiscount}
          onMinStorePriceChange={setMinStorePrice}
          onMaxStorePriceChange={setMaxStorePrice}
          onMinMsrpChange={setMinMsrp}
          onMaxMsrpChange={setMaxMsrp}
          onMinDiscountChange={setMinDiscount}
          showInStockOnly={showInStockOnly}
          onShowInStockOnlyChange={setShowInStockOnly}
          productStatusFilter={productStatusFilter}
          onProductStatusFilterChange={dealViewMode === "top" ? undefined : setProductStatusFilter}
          productStatusCounts={productStatusCounts}
          showSaved={!isFreeUser}
          onResetFilters={handleResetFilters}
          viewMode={headerViewMode}
          onViewModeChange={handleViewModeChange}
          hiddenProductsCount={hiddenProducts.length}
          onManageHiddenProducts={user?.userId ? () => setShowHiddenProductsModal(true) : undefined}
        />
      </div>}

      {/* Loading indicator under toolbar */}
      {!showFreeNearbyPreview && (isLoading || (productStatusFilter === "saved" && favoritesLoading)) && (
        <div className="flex-shrink-0 bg-white/90 dark:bg-dark-900/90 backdrop-blur-sm border-b border-gray-200/50 dark:border-dark-700/50 px-4 py-3">
          <div className="flex items-center justify-center gap-2.5">
            <div className="w-4 h-4 border-2 border-primary-200 dark:border-primary-800 border-t-primary-600 rounded-full animate-spin" />
            <span className="text-[13px] font-medium text-gray-600 dark:text-dark-200">
              {t("products.loadingProducts")}
            </span>
          </div>
        </div>
      )}

      {/* Product List */}
      <div
        key={retailerConfig.retailer}
        ref={listContainerRef}
        className={`flex-1 min-h-0 overflow-hidden p-0 m-0 relative touch-auto ${
          isLoading || isPending ? "opacity-60" : ""
        } ${
          isAnimatingSwipe && swipeDirection === "left"
            ? "transform -translate-x-full opacity-30 transition-transform duration-200 ease-out"
            : isAnimatingSwipe && swipeDirection === "right"
            ? "transform translate-x-full opacity-30 transition-transform duration-200 ease-out"
            : isSwiping
            ? "transition-none"
            : "transform translate-x-0 opacity-100 transition-transform duration-200 ease-out"
        }`}
        style={{
          transform:
            isSwiping && !isAnimatingSwipe
              ? `translateX(${swipeOffset}px)`
              : undefined,
        }}
      >
        {/* Refresh overlay */}
        {!showFreeNearbyPreview && isFetching && !isLoading && !browseQuery.isFetchingNextPage && (
          <div className="absolute top-3 inset-x-0 z-10 flex justify-center pointer-events-none">
            <div className="flex items-center gap-2 bg-white/95 dark:bg-dark-800/95 backdrop-blur-md rounded-full border border-gray-200/60 dark:border-dark-600/60 pl-3 pr-4 py-1.5 shadow-lg shadow-gray-900/5 dark:shadow-black/20">
              <div className="w-3.5 h-3.5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs font-semibold text-gray-700 dark:text-dark-100">
                {t("products.refreshingProducts")}
              </span>
            </div>
          </div>
        )}

        {/* Show loading skeletons, empty state, top deals gallery, or products list */}
        {showFreeNearbyPreview ? (
          <div
            ref={parentRef}
            data-products-scroll-container
            className="h-full w-full overflow-y-auto overscroll-contain bg-white pb-36 dark:bg-dark-950"
          >
            <NearbySavingsHero
              initialZip={user?.zipCode}
              onSeePlans={(zipCode) =>
                navigate(`/onboarding?intent=local&zip=${encodeURIComponent(zipCode)}`)
              }
            />
            {isLoading ? (
              <div className="space-y-2 px-3 sm:px-5">
                {Array.from({ length: 3 }).map((_, index) => <ProductSkeleton key={index} />)}
              </div>
            ) : (
              <div className="border-t border-stone-100 dark:border-stone-800">
                {filteredProducts.slice(0, 8).map((product, index) => (
                  <ProductItem
                    key={`${product.upc || product.sku || index}-nearby-preview`}
                    index={index}
                    style={PRODUCT_ITEM_LIST_STYLE}
                    data={listData}
                  />
                ))}
              </div>
            )}
          </div>
        ) : isLoading ? (
          dealViewMode === "top" ? (
            <TopProductsSkeleton />
          ) : (
            <div
              data-products-scroll-container
              className="w-full h-full overflow-auto scrollbar-modern touch-auto overscroll-contain p-2 bg-gray-50 dark:bg-dark-950"
            >
              <div className="space-y-2">
                {Array.from({ length: 10 }).map((_, index) => (
                  <ProductSkeleton key={index} />
                ))}
              </div>
            </div>
          )
        ) : isTopMode ? (
          visibleTopProducts.length > 0 ? (
            <TopProductsView
              model={topProductsModel}
              totalAllCount={allProductsCount ?? 0}
              storeFilterOptions={storeFilterOptions}
              onSeeAll={handleSeeAllFromTop}
              onSeeAllNew={handleSeeAllNewFromTop}
              onSelectCategory={handleTopSelectCategory}
              onCheckStockSearch={handleCheckStockSearch}
              retailer={retailerConfig.retailer}
              onHideProduct={user?.userId && !isFreeTier(user) ? handleHideProduct : undefined}
              isFreeUser={isFreeUser}
              communityPosts={communityPreviewQuery.data?.posts || []}
              communityLoading={communityPreviewQuery.isLoading}
              onOpenCommunity={() => navigate("/community-successes")}
              onUpgrade={() => navigate("/settings?tab=billing&upgradeSource=top_deals_community")}
              layoutMode={topLayout}
            />
          ) : (
            <div className="flex items-start justify-center h-full p-4 md:p-6 bg-gray-50 dark:bg-dark-950">
              <ScanningEmptyState retailer={retailer} isOnline={isOnlineRetailer} />
            </div>
          )
        ) : filteredProducts.length === 0 ? (
          <div className="flex items-start justify-center h-full p-4 md:p-6 bg-gray-50 dark:bg-dark-950">
            {(() => {
              const totalLoaded = productsData?.products?.length || 0;
              const hasActiveFilters =
                selectedCategories.length > 0 ||
                minStorePrice !== undefined ||
                maxStorePrice !== undefined ||
                minMsrp !== undefined ||
                maxMsrp !== undefined ||
                minDiscount !== undefined ||
                showNewestOnly ||
                showVariantsOnly ||
                hideCashback !== DEFAULT_HIDE_CASHBACK ||
                showHotDeals ||
                showPennyItems ||
                productStatusFilter !== "all" ||
                !showInStockOnly;
              const hasSearch = searchTerm.trim().length > 0;

              // The browse endpoint filters server-side, so a filtered-out result
              // arrives as an empty page (totalLoaded === 0) rather than a full one.
              if (hasActiveFilters || hasSearch) {
                return (
                  <div className="w-full max-w-sm mx-auto text-center pt-20">
                    <SlidersHorizontal size={32} className="mx-auto mb-4 text-gray-300 dark:text-dark-600" />
                    <p className="text-base font-semibold text-gray-900 dark:text-dark-50">
                      {t("products.noMatchingProducts")}
                    </p>
                    <p className="mt-1.5 text-sm text-gray-500 dark:text-dark-400">
                      {totalLoaded > 0
                        ? t("products.noMatchingProductsMessage", { count: totalLoaded })
                        : t("products.noMatchingProductsFiltersMessage")}
                    </p>
                    <button
                      onClick={handleResetFilters}
                      className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-xl font-medium text-sm hover:bg-primary-700 active:scale-[0.97] transition-all shadow-sm shadow-primary-600/20"
                    >
                      <RotateCcw size={15} />
                      {t("products.clearAllFilters")}
                    </button>
                  </div>
                );
              }

              // Nothing is filtered out: the stores haven't been scanned yet
              // (physical stores get the app's "Scanning your stores" screen).
              return <ScanningEmptyState retailer={retailer} isOnline={isOnlineRetailer} />;
            })()}
          </div>
        ) : viewMode === "gallery" ? (
          <div
            ref={parentRef}
            data-products-scroll-container
            onScroll={handleBrowseScroll}
            className="w-full h-full overflow-auto scrollbar-modern touch-auto touch-pan-y product-list-momentum overscroll-contain bg-[#F9F8FC] dark:bg-dark-950 px-2 pt-2 md:px-3"
          >
            {showFreeNearbyTeaser && (
              <NearbySavingsHero
                initialZip={user?.zipCode}
                onSeePlans={(zipCode) =>
                  navigate(`/onboarding?intent=local&zip=${encodeURIComponent(zipCode)}`)
                }
              />
            )}
            <div
              style={{
                height: `${galleryVirtualizer.getTotalSize() + 150}px`,
                width: "100%",
                position: "relative",
              }}
            >
              {galleryVirtualizer.getVirtualItems().map((row) => {
                const isTrendingRow = showTrendingListRow && row.index === 0;
                if (isTrendingRow) {
                  return (
                    <div
                      key="gallery-trending-row"
                      data-index={row.index}
                      ref={galleryVirtualizer.measureElement}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${row.start}px)`,
                        padding: "0",
                      }}
                    >
                      {renderTrendingProductsRow()}
                    </div>
                  );
                }
                const productRowIndex = showTrendingListRow ? row.index - 1 : row.index;
                const rowStart = productRowIndex * galleryColumns;
                const rowProducts = filteredProducts.slice(
                  rowStart,
                  rowStart + galleryColumns
                );
                return (
                  <div
                    key={`gallery-row-${row.index}`}
                    data-index={row.index}
                    ref={galleryVirtualizer.measureElement}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${row.start}px)`,
                      padding: "0",
                    }}
                  >
                    <div
                      className="grid gap-2.5 pb-2.5"
                      style={{
                        gridTemplateColumns: `repeat(${galleryColumns}, minmax(0, 1fr))`,
                      }}
                    >
                      {rowProducts.map((product, localIndex) => {
                        const productIndex = rowStart + localIndex;
                        return (
                          <GalleryProductCard
                            key={`${product.upc || product.sku || productIndex}-${productIndex}`}
                            product={product}
                            retailer={retailerConfig.retailer}
                            storeFilterOptions={storeFilterOptions}
                            onHideProduct={user?.userId && !isFreeTier(user) ? handleHideProduct : undefined}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // Show products list
          <div
            ref={parentRef}
            data-products-scroll-container
            onScroll={handleBrowseScroll}
            className="w-full h-full overflow-auto scrollbar-modern touch-auto touch-pan-y product-list-momentum overscroll-contain bg-gray-50 dark:bg-dark-950"
          >
            {showFreeNearbyTeaser && (
              <NearbySavingsHero
                initialZip={user?.zipCode}
                onSeePlans={(zipCode) =>
                  navigate(`/onboarding?intent=local&zip=${encodeURIComponent(zipCode)}`)
                }
              />
            )}
            <div
              style={{
                height: `${virtualizer.getTotalSize() + 150}px`,
                width: "100%",
                position: "relative",
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const isTrendingRow = showTrendingListRow && virtualItem.index === 0;
                const productIndex = showTrendingListRow
                  ? virtualItem.index - 1
                  : virtualItem.index;
                const product = filteredProducts[productIndex];
                const productKey = isTrendingRow
                  ? "trending-row"
                  : product
                  ? `${product.upc}-${productIndex}`
                  : `item-${virtualItem.index}`;

                const freeHintVariant =
                  isFreeUser && filteredProducts.length > 2 && productIndex >= 0
                    ? productIndex === 2
                      ? "local" as const
                      : productIndex === 14
                      ? "alerts" as const
                      : null
                    : null;

                return (
                  <div
                    key={productKey}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    {...(productIndex === 0 ? { "data-tour": "product-card" } : {})}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    {isTrendingRow ? (
                      renderTrendingProductsRow()
                    ) : (
                      <>
                    {freeHintVariant && (
                      <FreeHintRow
                        variant={freeHintVariant}
                        hiddenCount={hiddenOnlineProductsCount > 0 ? hiddenOnlineProductsCount : undefined}
                        zipCode={user?.zipCode}
                        t={t}
                        onUpgrade={() => navigate(freeHintVariant === "local" ? "/onboarding?intent=local" : `/settings?tab=billing&upgradeSource=inline_hint_${freeHintVariant}`)}
                      />
                    )}
                    <ProductItem
                      index={productIndex}
                      style={PRODUCT_ITEM_LIST_STYLE}
                      data={listData}
                    />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {browseEnabled && browseQuery.isFetchingNextPage && (
          <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center">
            <div className="flex items-center gap-2 rounded-full border border-gray-200/70 bg-white/95 px-4 py-2 text-xs font-semibold text-gray-700 shadow-lg backdrop-blur dark:border-dark-600 dark:bg-dark-800/95 dark:text-dark-100">
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
              {t("products.loadingProducts")}
            </div>
          </div>
        )}
        {browseEnabled && browseQuery.isFetchNextPageError && (
          <div className="absolute inset-x-0 bottom-4 z-20 flex justify-center">
            <button
              type="button"
              onClick={() => void browseQuery.fetchNextPage()}
              className="rounded-full border border-red-200 bg-white px-4 py-2 text-xs font-semibold text-red-600 shadow-lg dark:border-red-900/60 dark:bg-dark-800 dark:text-red-300"
            >
              {t("common.retry")}
            </button>
          </div>
        )}
      </div>

      {/* FAB for Refresh / Scroll to Top */}
      {!showFreeNearbyPreview && !isTopMode && filteredProducts.length > 0 && !isLoading && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (!isAtTop) {
              // Scroll to top then refresh
              const scrollElement = parentRef.current;
              if (scrollElement) {
                scrollElement.scrollTop = 0;
                if (virtualizerRef.current && filteredProducts.length > 0) {
                  virtualizerRef.current.scrollToIndex(0, { align: "start", behavior: "auto" });
                }
                requestAnimationFrame(() => {
                  if (scrollElement.scrollTop !== 0) scrollElement.scrollTop = 0;
                });
              }
              // Refresh after scroll animation
              setTimeout(() => handleRefresh(), 500);
            } else {
              // Just refresh
              handleRefresh();
            }
          }}
          disabled={isFetching}
          data-tour="refresh-fab"
          className="fixed z-50 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95 fab-button bg-white/75 dark:bg-dark-700/90 border border-primary-500/20 dark:border-primary-500/30 shadow-lg"
          style={{
            right: "24px",
            bottom: "24px",
          }}
          title={isAtTop ? t("products.refreshProducts") : t("products.backToTopRefresh")}
        >
          {isFetching ? (
            <svg className="w-5 h-5 animate-spin text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : isAtTop ? (
            <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          )}
        </button>
      )}

      {/* Swipe Hint */}
      {showSwipeHint && storeFilterOptions.length > 1 && (
        <div className="fixed bottom-32 left-1/2 transform -translate-x-1/2 z-40 pointer-events-none">
          <div className="bg-purple-600/25 backdrop-blur-sm text-purple-800 px-3 py-2 rounded-lg shadow-sm border border-purple-200/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  <svg
                    className="w-4 h-4 opacity-70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  <svg
                    className="w-4 h-4 opacity-70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
                <span className="text-xs font-medium opacity-80">
                  {t("products.swipeToSwitchStores")}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Zoho Widget */}
      <ZohoWidget
        showAboveGoUpButton={zohoWidgetProps.showAboveGoUpButton}
        isMobile={zohoWidgetProps.isMobile}
        hasBottomNav={zohoWidgetProps.hasBottomNav}
        instanceId="products"
      />

      {/* Zip Code Modal */}
      <ZipCodeModal
        isOpen={showZipCodeModal}
        onClose={() => {
          setShowZipCodeModal(false);
          setPendingSearch(null);
        }}
        onSuccess={handleZipCodeSuccess}
        currentZipCode={user?.zipCode || ""}
        title={t("products.setZipCode")}
        description={t("products.setZipCodeDescription")}
      />

      {showNearbyResultsModal && (
        <NearbyResults
          isSearching={isSearching}
          searchError={searchError}
          onClose={() => {
            setShowNearbyResultsModal(false);
            if (searchProductId) {
              handleSearchReset(searchProductId);
            }
          }}
          searchResults={searchResults}
          homeDepotSearchResults={homeDepotSearchResults}
          isHomeDepot={retailerConfig.retailer === "home_depot"}
          retailer={retailerConfig.retailer}
          title={t("products.storeAvailability")}
          amazonData={searchAmazonData || undefined}
        />
      )}

      {/* Penny Items Disclaimer Modal */}
      <PennyItemsDisclaimerModal
        isOpen={showPennyItemsDisclaimer}
        onClose={handleDisclaimerClose}
        onAccept={handleDisclaimerAccept}
        retailer={retailerConfig.retailer}
      />

      <PennyModeChooser
        isOpen={canUsePennyBeta && showPennyModeChooser}
        onClose={() => setShowPennyModeChooser(false)}
        onSelectBeta={() => {
          setShowPennyModeChooser(false);
          navigate("/home_depot/penny-items-beta");
        }}
        onSelectPro={() => {
          setShowPennyModeChooser(false);
          navigate("/home_depot/penny-items-pro");
        }}
      />

      {/* Bot Filters Onboarding Modal */}
      <BotFiltersOnboardingModal
        isOpen={showOnboardingModal}
        onComplete={handleOnboardingComplete}
        onClose={handleOnboardingClose}
      />

      {extraStoreUndo && extraStoreUndo.retailer === retailerConfig.retailer && (
        <HiddenProductToast
          key={`extra-store:${extraStoreUndo.storeId}:${extraStoreUndo.at}`}
          message={t("products.extraStoreHiddenToast")}
          undoLabel={t("products.undoHideExtraStore")}
          onUndo={handleUndoHideExtraStore}
          onDismiss={dismissExtraStoreUndo}
          zIndex={100001}
        />
      )}

      {hiddenUndo && hiddenUndo.userId === user?.userId && hiddenUndo.product.retailer === retailerConfig.retailer && (
        <HiddenProductToast
          key={`${hiddenUndo.product.productId}:${hiddenUndo.product.hiddenAt}`}
          message={t("products.productHidden", { retailer: getRetailerLabel(retailerConfig.retailer) })}
          undoLabel={t("products.undoHideProduct")}
          onUndo={() => void handleRestoreHiddenProduct(hiddenUndo.product)}
          onDismiss={dismissHiddenUndo}
        />
      )}

      <HiddenProductsModal
        open={showHiddenProductsModal}
        products={hiddenProducts}
        retailerLabel={retailerLabel}
        restoringProductId={restoringHiddenProductId}
        restoringAll={restoringAllHiddenProducts}
        onClose={() => setShowHiddenProductsModal(false)}
        onRestore={(product) => void handleRestoreHiddenProduct(product)}
        onRestoreAll={() => void handleRestoreAllHiddenProducts()}
      />

      <ReferralInviteModal
        open={showReferralInviteModal}
        isSaving={savingReferralInviteDismissal}
        onDismiss={() => void finishReferralInviteModal(false)}
        onInvite={() => void finishReferralInviteModal(true)}
      />

			{queryResumeIntent &&
				(queryResumeIntent.action === "save" || queryResumeIntent.action === "history") &&
				createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/50 sheet-backdrop-in"
            role="dialog"
            aria-label={queryResumeIntent.action === "save" ? t("card.resumeSavingDeal") : t("card.resumePriceHistory")}
            onPointerDown={(event) => {
              if (event.target === event.currentTarget) closeQueryResumeIntent();
            }}
          >
            <div
              className="h-[78vh] w-full overflow-hidden rounded-t-2xl bg-white shadow-2xl dark:bg-dark-900 sheet-panel-in"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <ProductDetailModal
                sku={queryResumeIntent.productId}
                storeId="online"
                retailer={retailerConfig.retailer === "walmart_online" ? "walmart" : retailerConfig.retailer}
                onClose={closeQueryResumeIntent}
                scrollToChart={queryResumeIntent.action === "history"}
                resumeAction={queryResumeIntent.action}
              />
            </div>
          </div>,
          document.body
        )}

      {/* First visit to local deals in grid view: "How to read a deal" (replaces the auto tour). */}
      <DealCardGuide
        userId={user?.userId}
        ready={
          viewMode === "gallery" &&
          dealViewMode === "all" &&
          !isFreeUser &&
          !isOnlineRetailer &&
          productStatusFilter !== "saved" &&
          !showPennyItems &&
          !showFreeNearbyPreview &&
          !isLoading &&
          !error &&
          filteredProducts.length > 0 &&
          !showOnboardingModal &&
          !showZipCodeModal &&
          !showPennyItemsDisclaimer &&
          !showPennyModeChooser &&
          !showHiddenProductsModal &&
          !showNearbyResultsModal &&
          !queryResumeIntent
        }
      />
    </div>
    </CheckStockContext.Provider>
  );
}
